/**
 * Real-account data hooks.
 *
 * First wiring of the `whbFetch` same-origin proxy into the React tree. Each
 * hook in this file is a thin `useState` + `useEffect` wrapper — not
 * react-query — because the app hasn't adopted a caching layer yet and the
 * first goal is simply to prove the plumbing works end-to-end:
 *
 *    PWA hook → whbFetch('/api/<path>') → Cloudflare proxy → WHB API → JSON
 *
 * Once we've seen real responses land for each endpoint, the plan is to
 * replace these with typed react-query hooks and retire the mock-data
 * imports screen-by-screen. Until then, every page continues to render
 * mock data; these hooks only feed the debug panels flagged behind
 * `kind === "real"`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { whbFetch } from "@/lib/auth";
import { isTradingDay } from "@/lib/trading-calendar";
import { useAuthStore } from "@/store/auth-store";
import { useAccountVisibilityStore } from "@/store/account-visibility-store";
import {
  useAccountNicknamesStore,
  applyAccountNicknames,
} from "@/store/account-nicknames-store";
import {
  cacheGet,
  cacheMergeById,
  cacheSet,
  cacheKey,
  cacheDelete,
  cacheListByPrefix
} from "@/lib/whb-cache";
import { loadCorrections } from "@/lib/whb-corrections";
import type { Bot, BrokerAccount, Cashflow } from "@/data/mock-data";
import type { Position } from "@/data/mock-positions";
import type { Order } from "@/data/mock-orders";
import {
  mapRealBotList,
  mapRealBrokerAccounts,
  mapRealBrokerAccountDetail,
  mapRealStocksToPositions,
  mapRealTradeToOrder,
  mapRealTradingHistory,
  mapRealMissedOrders,
  unwrapActivities,
  type RawAccountActivity,
  type RawBrokerAccountDetail,
  type RawTradingHistoryRow,
  type RawTradingHistoryResponse,
} from "@/lib/whb-accounts-adapter";
import {
  reconstructBotEquityCurve,
  uniqueTickersFromOrders,
  type HistoricalQuotesMap,
  type HistoricalSplitsMap,
  type TickerSplit,
} from "@/lib/equity-reconstruction";

/**
 * Shape of the state every real-data hook exposes. `data` is typed
 * generically so each caller can narrow it once upstream shapes are
 * captured in docs/REAL-APP-NOTES.md.
 */
export interface RealDataState<T> {
  /** Response body decoded from JSON, or `null` until the request lands. */
  data: T | null;
  /** Error message from the fetch wrapper or upstream non-2xx body. */
  error: string | null;
  /** `true` from mount/kind-flip until the first response (success or fail). */
  loading: boolean;
  /** Wall-clock ms the request took, handy for the debug panel. */
  durationMs: number | null;
}

/**
 * Options for {@link useRealApi}.
 *
 *   • `cacheKey` — when set, the hook reads IndexedDB first and
 *     paints the previous response before the network request
 *     finishes. Successful responses are written back to the cache.
 *     Use {@link cacheKey} helpers for consistent naming.
 *   • `mergeById` — for list-shaped payloads. Instead of replacing
 *     the cached array, the new rows are upserted by `id` so any
 *     older rows the server may have since pruned stay visible.
 *     The hook's `data` is the merged array.
 */
export interface RealApiOptions {
  cacheKey?: string;
  mergeById?: boolean;
}

/**
 * Fetch `/api/<path>` when the user is signed in with their real WHB
 * account. Re-runs when `kind` flips from "demo" → "real" (or vice versa)
 * and when `path` changes. Demo sessions short-circuit to idle state —
 * we never hit the proxy under a demo session, which would 401 and kick
 * the user through a bridge re-login loop they didn't ask for.
 *
 * When `cacheKey` is supplied, first paint comes from IndexedDB (if
 * present) so reloads and re-logins don't show a loading spinner.
 * Fresh data overwrites the cached copy on a successful response.
 */
export function useRealApi<T = unknown>(
  path: string,
  opts: RealApiOptions = {}
): RealDataState<T> {
  const { cacheKey: key, mergeById } = opts;
  const kind = useAuthStore((s) => s.kind);
  const idle = kind !== "real" || !path;
  const [state, setState] = useState<RealDataState<T>>({
    data: null,
    error: null,
    loading: !idle,
    durationMs: null,
  });

  useEffect(() => {
    // Either the user isn't signed in with a real account, or the
    // caller is explicitly idling this hook (empty path — used by
    // detail hooks when no account is selected). Reset to idle state
    // so a prior populated value doesn't leak into the new context.
    if (idle) {
      setState({ data: null, error: null, loading: false, durationMs: null });
      return;
    }

    let cancelled = false;
    const start = performance.now();
    // Keep `loading: true` so any consumer watching the spinner sees
    // movement, but seed `data` from cache asynchronously so the
    // first paint can render a prior response. `error` gets cleared.
    setState((s) => ({ ...s, loading: true, error: null }));

    // Cache-first paint (best-effort, no-throw).
    if (key) {
      void cacheGet<T>(key).then((hit) => {
        if (cancelled || !hit) return;
        setState((s) =>
          // Network request may have already landed — don't overwrite
          // fresher data with a cache hit.
          s.data === null
            ? { data: hit.value, error: null, loading: true, durationMs: null }
            : s
        );
      });
    }

    (async () => {
      try {
        const body = await whbFetch<T>(`/api${path.startsWith("/") ? path : `/${path}`}`);
        if (cancelled) return;
        let finalBody: T = body;
        if (key) {
          if (mergeById && Array.isArray(body)) {
            finalBody = (await cacheMergeById(
              key,
              body as ReadonlyArray<{ id: string }>
            )) as unknown as T;
          } else {
            await cacheSet(key, body);
          }
        }
        setState({
          data: finalBody,
          error: null,
          loading: false,
          durationMs: Math.round(performance.now() - start),
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          // Keep any cached data we painted earlier so the UI doesn't
          // flash back to empty on a transient network error.
          data: s.data,
          error: (err as Error).message ?? String(err),
          loading: false,
          durationMs: Math.round(performance.now() - start),
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [idle, path, key, mergeById]);

  return state;
}

/**
 * Convenience wrapper for the accounts landing page. WHB's production app
 * calls the response "broker accounts" and lands there after login — we
 * mirror the same path so the proxy forwards `/api/broker-accounts` →
 * `https://white-hat-bots-api.azurewebsites.net/broker-accounts`.
 */
export function useRealBrokerAccounts<T = unknown>(): RealDataState<T> {
  // Cache the raw list response so the accounts dropdown paints
  // immediately on refresh. The list's own shape (array of rows
  // keyed by id) is amenable to merge-by-id, but in practice the
  // backend returns a stable list — a plain replace is simpler.
  return useRealApi<T>("/broker-accounts", { cacheKey: cacheKey.accountList() });
}

/**
 * Fetch the full `/bots` list for the signed-in user.
 *
 * One call, no pagination — the list is cross-account. Caller filters
 * by `account.botIds` to get the subset attached to the current
 * account. Cached in IndexedDB under `bots-list:root` for instant
 * paint on reload.
 *
 * Return shape wraps the decoded PWA `Bot[]` (identity fields already
 * mapped via `mapRealBotList`). The `accountId` on each Bot is left
 * blank — the list endpoint doesn't carry it and the caller
 * assigns it after joining against `account.botIds`.
 */
export function useRealBots(): {
  bots: Bot[] | null;
  loading: boolean;
  error: string | null;
} {
  const raw = useRealApi<unknown>("/bots", { cacheKey: cacheKey.botsList() });
  const bots = useMemo<Bot[] | null>(() => {
    if (raw.data === null) return null;
    return mapRealBotList(raw.data);
  }, [raw.data]);

  // Persist a tiny registry of every bot we've ever seen — enables the
  // Bots tab's "Archived" section to recover names after deletion (the
  // API drops the row entirely so neither /bots list nor /bots/{id}
  // detail keep the human-readable name once the bot is gone).
  // Updates merge-style: existing entries' `accountId` is preserved
  // when the latest /bots row didn't carry one. Sergey 2026-04-30.
  useEffect(() => {
    if (!bots || bots.length === 0) return;
    void (async () => {
      const existing =
        (await cacheGet<Record<string, BotRegistryEntry>>(cacheKey.botRegistry()))?.value ?? {};
      const todayIso = new Date().toISOString().slice(0, 10);
      let dirty = false;
      for (const b of bots) {
        const prev = existing[b.id];
        if (
          !prev ||
          prev.name !== b.name ||
          prev.lastSeenIso !== todayIso
        ) {
          existing[b.id] = {
            name: b.name,
            // accountId not available on /bots list rows. We try to
            // backfill from `bot-detail:{id}.brokerAccountId` — if a
            // detail blob has been cached this session, its accountId
            // wins. Otherwise keep prev or empty string.
            accountId: prev?.accountId ?? "",
            lastSeenIso: todayIso
          };
          dirty = true;
        }
      }
      if (dirty) await cacheSet(cacheKey.botRegistry(), existing);
    })();
  }, [bots]);

  return { bots, loading: raw.loading, error: raw.error };
}

/** Persistent registry entry — see `cacheKey.botRegistry()`. */
export interface BotRegistryEntry {
  /** Last-seen human name from /bots list. */
  name: string;
  /** Parent broker account id. Resolved from
   *  `bot-detail:{id}.brokerAccountId` when first available; empty
   *  until a detail fetch lands. */
  accountId: string;
  /** ISO yyyy-mm-dd of the day we last saw this bot in /bots. The
   *  Archived view treats `lastSeenIso !== today` as "not active" once
   *  the current /bots fetch has resolved. */
  lastSeenIso: string;
}

/**
 * Per-bot weekly $/% change derived from `/broker-accounts/{id}.states[]`.
 * `fraction` is a decimal (0.013 → +1.30%); `delta` is the dollar change.
 */
export interface BotWeeklyDelta {
  fraction: number;
  delta: number;
}

/**
 * Compute the Monday 00:00 UTC anchor for the current calendar week.
 * Mon-Fri-style "this week" — if today is Mon, the window is one
 * trading day so far. The comparison row is the latest state with
 * `timestamp < monday`, which is typically last Friday's close.
 */
function mondayOfThisWeekUTC(now: Date = new Date()): number {
  const dow = now.getUTCDay(); // 0=Sun … 6=Sat
  const daysSinceMonday = (dow + 6) % 7; // Mon→0, Tue→1, …, Sun→6
  return Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - daysSinceMonday,
    0,
    0,
    0,
    0
  );
}

/**
 * Per-bot detail-shape (subset of `/bots/{id}` we read here). The
 * endpoint also returns `placedOrders[]`, `stocks[]`, `dailyProfits[]`
 * etc. — we only declare what this hook touches.
 */
interface RawBotDetail {
  id?: string;
  statusDate?: string;
  /** Parent broker account id — required when calling /trading-history
   *  with a `botId` filter. Lifted from `/bots/{id}.brokerAccountId`. */
  brokerAccountId?: string;
  portfolio?: {
    equity?: number;
    allocatedAmount?: number;
    buyingPower?: number;
    /** Bot's idle cash slice — the bot's "earmarked" portion of the
     *  account's cash balance (positive when bot is sitting on cash;
     *  may go negative for margin-using bots). Probed live 2026-04-30
     *  on Default Live's bot detail. Used by `Cash excl bots` in
     *  BalancesView. */
    cash?: number;
    totalProfit?: number;
    totalProfitPercent?: number;
  };
  states?: Array<{
    botId?: string | null;
    timestamp?: string;
    cash?: number;
    stocks?: number;
    margin?: number;
  }>;
  stocks?: Array<{ symbol?: string }>;
  dailyProfits?: Array<{ date?: string; profit?: number }>;
  /** **NOT** the bot's full trading history — verified 2026-04-26 to
   *  contain only buy-side fills for currently-held positions
   *  (Live E shows 30 entries here vs the real 483 trades from
   *  `/trading-history?botId=…`, missing every sell and every
   *  closed-out lot). Kept on the type for completeness but the
   *  reconstruction explicitly does NOT consume this — see
   *  `useRealBotOrders` below. The row shape itself also differs
   *  (`placedAtUtc`, `unitPrice`, `totalSum` instead of `date`,
   *  `price`, `total`), so even merging would require a custom
   *  mapper. Don't bother. */
  placedOrders?: Array<{
    symbol?: string;
    placedAtUtc?: string;
    qty?: number;
    totalSum?: number;
    unitPrice?: number;
    action?: number;
  }>;
}

/**
 * Numbers we extract per bot — populated by `useRealBotDetailsBulk`
 * from `/bots/{id}` and applied by the BotsPanel as an overlay over
 * each `Bot` record. All fields optional because partial responses
 * (e.g. cache-only first paint) shouldn't block surfacing what's
 * available.
 */
export interface BotOverlay {
  value?: number;
  buyingPower?: number;
  /** Bot's idle cash slice — Σ across all bots feeds the
   *  `Cash excl bots` calculation in BalancesView. Sourced from
   *  `/bots/{id}.portfolio.cash`. */
  cash?: number;
  totalReturnUsd?: number;
  totalReturnFraction?: number;
  dayChangeFraction?: number;
  weeklyChangeFraction?: number;
  weeklyChangeDelta?: number;
  apyFraction?: number;
  sharpe?: number;
  maxDDFraction?: number;
  /** Last ~60 days of cash+stocks values, oldest first — feeds the
   *  per-bot row sparkline so real bots look identical to demo bots. */
  sparkline?: number[];
  /** Full daily reconstructed curve (oldest first) — dates + equity
   *  per trading day from inception to today. Used by the bot drawer's
   *  range selector to slice between 3M / YTD / 1Y / ALL views.
   *  Sergey 2026-05-01: previously the drawer was limited to the 60-day
   *  sparkline so longer ranges read as flat — exposing the full
   *  curve lets the user inspect lifetime performance the same way
   *  the archived-bot drawer already does. */
  fullCurve?: ReadonlyArray<{ date: string; equity: number }>;
  /** Allocated amount at inception, surfaced from `/bots/{id}.portfolio.allocatedAmount`.
   *  Used by the drawer chart as the baseline for the underwater
   *  shading (above = bot grew the allocation, below = currently
   *  underwater). Distinct from `Bot.allocatedAmount` which can be
   *  shadowed by the list-row default. Sergey 2026-05-01. */
  chartBaseline?: number;
  /** Symbols currently held by the bot (from `/bots/{id}.stocks[]`).
   *  Used in the drawer's "Universe" row; real bots otherwise have an
   *  empty tickers list from `/bots`. */
  tickers?: string[];
}

/**
 * Build a per-bot overlay sourced from `/bots/{id}` (one detail call
 * per bot). Probed live 2026-04-26 — that endpoint returns a full
 * per-bot snapshot:
 *
 *   • `portfolio.equity` — current $ value of the bot's slice
 *     (replaces the `/broker-accounts/{id}.bots[].value` source we
 *     used previously).
 *   • `states[]` — 700+ rows of daily {cash, stocks, margin} keyed by
 *     this bot. The Mon-Fri "this week" delta compares the latest
 *     (current) row's cash+stocks to the latest pre-Monday row's
 *     cash+stocks.
 *
 * (`/broker-accounts/{id}.states[]` only carries account-level rows
 * with `botId: null`, so it can't power per-bot weekly deltas — see
 * 2026-04-26 recon updates in WHB-API-RECON.md.)
 *
 * Cache-first paint via IndexedDB; network refresh runs in parallel
 * and writes back through the cache. Idle for demo sessions and when
 * `botIds` is empty.
 */
export function useRealBotDetailsBulk(botIds: readonly string[]): {
  overlays: Record<string, BotOverlay>;
  /** Convenience: a `Record<botId, value>` distilled from `overlays`
   *  for callers that only need the current value. */
  values: Record<string, number>;
  /** Convenience: per-bot weekly delta for the simple-view list. */
  weeklyDeltas: Record<string, BotWeeklyDelta>;
  loading: boolean;
} {
  const kind = useAuthStore((s) => s.kind);
  const idle = kind !== "real" || botIds.length === 0;

  // Sort + join for a stable dependency key — different orderings of
  // the same id list shouldn't trigger a refetch.
  const key = useMemo(() => [...botIds].sort().join(","), [botIds]);

  // We split state into "raw bot details" (fetched async) and the
  // derived overlay (computed via useMemo from rawByBot + prices).
  // This split is what lets the per-bot equity reconstruction
  // (which needs historical prices) recompute reactively when the
  // prices hook below resolves.
  const [rawState, setRawState] = useState<{
    rawByBot: Record<string, RawBotDetail>;
    loading: boolean;
    key: string;
  }>({ rawByBot: {}, loading: !idle, key: "" });

  useEffect(() => {
    if (idle) {
      setRawState({ rawByBot: {}, loading: false, key });
      return;
    }
    let cancelled = false;
    setRawState((s) => ({ ...s, loading: true, key }));
    const ids = key.split(",").filter(Boolean);

    const merge = (
      parts: Array<readonly [string, RawBotDetail] | null>
    ): Record<string, RawBotDetail> => {
      const acc: Record<string, RawBotDetail> = {};
      for (const p of parts) {
        if (!p) continue;
        acc[p[0]] = p[1];
      }
      return acc;
    };

    // Cache-first paint via per-bot-detail cache.
    void Promise.all(
      ids.map(async (id) => {
        const hit = await cacheGet<RawBotDetail>(cacheKey.botDetail(id));
        return hit && hit.value ? ([id, hit.value] as const) : null;
      })
    ).then((cached) => {
      if (cancelled) return;
      const m = merge(cached);
      if (Object.keys(m).length > 0) {
        setRawState((s) =>
          s.loading ? { rawByBot: m, loading: true, key } : s
        );
      }
    });

    // Network refresh — `/api/bots/{id}` per bot. Each detail blob is
    // ~30-50KB; we fan out so a slow account doesn't block the others.
    // Errors per-bot are swallowed.
    void Promise.all(
      ids.map(async (id) => {
        try {
          const raw = await whbFetch<RawBotDetail>(
            `/api/bots/${encodeURIComponent(id)}`
          );
          void cacheSet(cacheKey.botDetail(id), raw);
          // Backfill the bot registry's accountId field once we know
          // it. /bots list rows don't carry the parent account, so
          // until a detail fetch lands, the registry has accountId="".
          // Required for the Archived view to filter by current
          // account. Sergey 2026-04-30.
          //
          // Snapshot brokerAccountId into a local `const` so the type
          // narrows correctly through the inner async closure — TS
          // would otherwise widen `raw.brokerAccountId` back to
          // `string | undefined` inside the IIFE.
          const brokerAccountId =
            raw && typeof raw.brokerAccountId === "string" && raw.brokerAccountId
              ? raw.brokerAccountId
              : null;
          if (brokerAccountId) {
            void (async () => {
              const reg =
                (await cacheGet<Record<string, BotRegistryEntry>>(cacheKey.botRegistry()))?.value ?? {};
              const prev = reg[id];
              if (!prev || prev.accountId !== brokerAccountId) {
                reg[id] = {
                  name: prev?.name ?? id,
                  accountId: brokerAccountId,
                  lastSeenIso:
                    prev?.lastSeenIso ?? new Date().toISOString().slice(0, 10)
                };
                await cacheSet(cacheKey.botRegistry(), reg);
              }
            })();
          }
          return raw ? ([id, raw] as const) : null;
        } catch {
          return null;
        }
      })
    ).then((fresh) => {
      if (cancelled) return;
      setRawState({ rawByBot: merge(fresh), loading: false, key });
    });

    return () => {
      cancelled = true;
    };
  }, [idle, key]);

  // Per-bot full trading history — fetched from /trading-history with
  // a `botId` filter. **Critical**: this replaces /bots/{id}.placedOrders
  // (verified 2026-04-26 to be a partial open-lots view, missing every
  // sell — 30 entries vs 483 actual trades on Live E). Reconstruction
  // can't see drawdowns without sells because positions never close.
  const [ordersByBot, setOrdersByBot] = useState<Record<string, Order[]>>({});

  useEffect(() => {
    if (idle) {
      setOrdersByBot({});
      return;
    }
    // Don't fetch until we have a bot's brokerAccountId — /trading-history
    // requires it. Skip bots whose detail hasn't landed.
    const targets = Object.entries(rawState.rawByBot)
      .filter(
        ([, d]) =>
          typeof d.brokerAccountId === "string" && d.brokerAccountId.length > 0
      )
      .map(([id, d]) => ({ botId: id, accountId: d.brokerAccountId! }));
    if (targets.length === 0) return;

    let cancelled = false;

    // Cache-first paint per bot.
    void Promise.all(
      targets.map(async ({ botId }) => {
        const hit = await cacheGet<RawTradingHistoryRow[]>(
          cacheKey.botOrders(botId)
        );
        if (!hit || !Array.isArray(hit.value)) return null;
        const mapped: Order[] = [];
        for (const row of hit.value) {
          const o = mapRealTradeToOrder(row);
          if (o) mapped.push(o);
        }
        return [botId, mapped] as const;
      })
    ).then((arr) => {
      if (cancelled) return;
      const merged: Record<string, Order[]> = {};
      for (const e of arr) if (e) merged[e[0]] = e[1];
      if (Object.keys(merged).length > 0) {
        setOrdersByBot((prev) => ({ ...prev, ...merged }));
      }
    });

    // Network refresh per bot. POST /trading-history with botId filter.
    // pageSize: 10000 — Live S has ~2.2k trades and we want them in one
    // shot. The recon doc confirms server returns all rows under a
    // single big page when totalItems ≤ pageSize.
    void Promise.all(
      targets.map(async ({ botId, accountId }) => {
        try {
          const resp = await whbFetch<RawTradingHistoryResponse>(
            `/api/trading-history`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                brokerAccountId: accountId,
                botId,
                page: 1,
                pageSize: 10000,
                startDate: "2020-01-01",
                endDate: new Date().toISOString().slice(0, 10),
                symbols: "",
                action: null,
                strategy: null,
                state: null,
              }),
            }
          );
          const rows = Array.isArray(resp?.records) ? resp.records! : [];
          void cacheSet(cacheKey.botOrders(botId), rows);
          const mapped: Order[] = [];
          for (const row of rows) {
            const o = mapRealTradeToOrder(row);
            if (o) mapped.push(o);
          }
          return [botId, mapped] as const;
        } catch {
          return null;
        }
      })
    ).then((arr) => {
      if (cancelled) return;
      const merged: Record<string, Order[]> = {};
      for (const e of arr) if (e) merged[e[0]] = e[1];
      // Replace, not merge — fresh network response is authoritative.
      setOrdersByBot(merged);
    });

    return () => {
      cancelled = true;
    };
    // We depend on the *set of bots* (from rawByBot), not its full
    // contents — comparing `key` (sorted, joined botIds) keeps the
    // effect from re-firing on every cache rewrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idle, key, Object.keys(rawState.rawByBot).sort().join(",")]);

  // Tickers across every bot's full order history + currently-held
  // stocks — sourced from `/trading-history` (real history) NOT from
  // /bots/{id}.placedOrders (partial open-lots view). Sorted for a
  // stable dependency key so the prices hook below doesn't refetch
  // on insertion-order shuffles.
  const tickers = useMemo(() => {
    const set = new Set<string>();
    for (const orders of Object.values(ordersByBot)) {
      for (const o of orders) {
        if (typeof o.asset === "string" && o.asset.length > 0) {
          set.add(o.asset.toUpperCase());
        }
      }
    }
    for (const detail of Object.values(rawState.rawByBot)) {
      if (Array.isArray(detail.stocks)) {
        for (const s of detail.stocks) {
          if (s && typeof s.symbol === "string" && s.symbol) {
            set.add(s.symbol.toUpperCase());
          }
        }
      }
    }
    return Array.from(set).sort();
  }, [rawState.rawByBot, ordersByBot]);

  // Historical prices + splits — same waterfall as account-level
  // reconstruction. When prices are still loading, the overlays
  // memo below produces curves that fall back to cost-basis pricing
  // for unquoted tickers (engine handles this internally) so the UI
  // surfaces *something* on first paint instead of stalling.
  const { prices, splits } = useRealHistoricalPrices(tickers);

  // Build the per-bot overlay from raw detail + orders + prices/splits.
  // Recomputes whenever any input changes; the heavy work
  // (reconstructEquityCurve walks ~700 days × N tickers) is gated
  // behind the deps so re-renders that don't change inputs are free.
  const overlays = useMemo(() => {
    const monday = mondayOfThisWeekUTC();
    const out: Record<string, BotOverlay> = {};
    for (const [id, detail] of Object.entries(rawState.rawByBot)) {
      const orders = ordersByBot[id] ?? [];
      out[id] = extractBotOverlay(detail, orders, monday, prices, splits);
    }
    return out;
  }, [rawState.rawByBot, ordersByBot, prices, splits]);

  // Distill convenience maps from the rich overlay so existing callers
  // (BotsSimpleView, BotsPanel) don't need to know the full shape.
  const values = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [id, o] of Object.entries(overlays)) {
      if (typeof o.value === "number") out[id] = o.value;
    }
    return out;
  }, [overlays]);
  const weeklyDeltas = useMemo(() => {
    const out: Record<string, BotWeeklyDelta> = {};
    for (const [id, o] of Object.entries(overlays)) {
      if (
        typeof o.weeklyChangeFraction === "number" &&
        typeof o.weeklyChangeDelta === "number"
      ) {
        out[id] = {
          fraction: o.weeklyChangeFraction,
          delta: o.weeklyChangeDelta,
        };
      }
    }
    return out;
  }, [overlays]);

  return {
    overlays,
    values,
    weeklyDeltas,
    loading: rawState.loading,
  };
}

/**
 * Pure helper — turn a `/bots/{id}` raw response into a `BotOverlay`,
 * given the latest historical prices/splits for the bot's tickers.
 *
 * **Why a free function and not a closure inside the hook:** the
 * overlay needs to recompute when prices arrive, which is asynchronous
 * relative to the raw-detail fetch. Splitting it into a pure function
 * lets the parent hook keep raw details in `useState` (network
 * lifecycle) and derived overlays in `useMemo` (recomputed on prices
 * landing) without re-fetching the network response on every prices
 * update.
 *
 * **Metric sources** (TradingView convention; see
 * `feedback_whb_metrics_match_tradingview` memory):
 *   • value, buyingPower, totalReturnUsd / Fraction — passthroughs
 *     from `portfolio.{equity, buyingPower, totalProfit, …}`.
 *   • dayChangeFraction, weeklyChangeFraction/Delta — derived from
 *     `states[]` (broker-snapshot cash+stocks). These are short-term
 *     diffs and tolerate the snapshot-glitch noise that wrecks DD.
 *   • maxDDFraction, sharpe, sparkline — derived from a *reconstructed*
 *     equity curve walked day-by-day from `placedOrders[]` and
 *     historical closes (mirrors `mapRealBrokerAccountDetail`'s
 *     account-level logic). The broker's `states[]` series is
 *     unusable for these per Sergey 2026-04-26: verified single-day
 *     -38 % / +127 % snapshot artifacts contaminate it. Reconstruction
 *     gives a true mark-to-market curve that catches unrealized
 *     drawdowns dailyProfits[] would miss (it's realized-only on
 *     this dataset).
 *   • tickers — current holdings from `stocks[]`.
 *   • apyFraction — annualised totalReturnFraction over bot age.
 */
function extractBotOverlay(
  detail: RawBotDetail,
  orders: readonly Order[],
  monday: number,
  prices: HistoricalQuotesMap,
  splits: HistoricalSplitsMap
): BotOverlay {
  const out: BotOverlay = {};

  // Portfolio fields — direct passthroughs from the API.
  if (detail.portfolio) {
    const p = detail.portfolio;
    if (typeof p.equity === "number" && Number.isFinite(p.equity)) {
      out.value = p.equity;
    }
    if (
      typeof p.buyingPower === "number" &&
      Number.isFinite(p.buyingPower)
    ) {
      out.buyingPower = p.buyingPower;
    }
    if (typeof p.cash === "number" && Number.isFinite(p.cash)) {
      out.cash = p.cash;
    }
    if (
      typeof p.totalProfit === "number" &&
      Number.isFinite(p.totalProfit)
    ) {
      out.totalReturnUsd = p.totalProfit;
    }
    if (
      typeof p.totalProfitPercent === "number" &&
      Number.isFinite(p.totalProfitPercent)
    ) {
      // API returns percentage points (4.45 means 4.45%); store
      // as a decimal fraction (0.0445) to match Bot.allTimeGainPct
      // and the rest of the app's convention.
      out.totalReturnFraction = p.totalProfitPercent / 100;
    }
  }

  // States series — short-term day/week diffs only. We deliberately
  // do NOT walk this for drawdown anymore; the broker-snapshot series
  // carries single-day glitches that contaminate peak-to-trough math.
  if (Array.isArray(detail.states) && detail.states.length > 0) {
    const series: Array<{ ts: number; v: number }> = [];
    for (const s of detail.states) {
      if (!s || typeof s.timestamp !== "string") continue;
      const ts = Date.parse(s.timestamp);
      if (!Number.isFinite(ts)) continue;
      const cash = typeof s.cash === "number" ? s.cash : 0;
      const stocks = typeof s.stocks === "number" ? s.stocks : 0;
      series.push({ ts, v: cash + stocks });
    }
    series.sort((a, b) => a.ts - b.ts);

    if (series.length > 0) {
      const latest = series[series.length - 1];
      if (out.value === undefined) out.value = latest.v;

      // Day change — latest vs prior row, but ONLY when today is
      // actually a trading day (NYSE Mon–Fri excluding holidays).
      // On weekends and holidays the broker may still post tiny
      // snapshot deltas (corp actions, dividend marks) but those
      // aren't "today's market move" — surfacing them as "▲ $X
      // today" misleads. Suppress here so the simple-view falls
      // through to the weekly delta. (Sergey 2026-04-27.)
      const todayIso = new Date().toISOString().slice(0, 10);
      if (series.length >= 2 && isTradingDay(todayIso)) {
        const prev = series[series.length - 2];
        if (prev.v > 0) {
          out.dayChangeFraction = (latest.v - prev.v) / prev.v;
        }
      }

      // Weekly — latest vs latest row before Monday cutoff.
      let mondayPriorIdx = -1;
      for (let i = series.length - 1; i >= 0; i--) {
        if (series[i].ts < monday) {
          mondayPriorIdx = i;
          break;
        }
      }
      if (mondayPriorIdx >= 0 && series[mondayPriorIdx].v > 0) {
        const priorV = series[mondayPriorIdx].v;
        const delta = latest.v - priorV;
        out.weeklyChangeDelta = delta;
        out.weeklyChangeFraction = delta / priorV;
      }
    }
  }

  // Max drawdown + Sharpe + sparkline — reconstructed mark-to-market
  // equity curve from inception. Sources:
  //   • Cashflow: synthetic single deposit at the bot's first
  //     activity (statusDate or first order date — whichever is
  //     earliest, see below).
  //   • Orders: `/trading-history?botId=…` (passed in from parent
  //     hook; the embedded /bots/{id}.placedOrders is a partial
  //     open-lots view that misses every sell, verified 2026-04-26).
  //   • Prices/splits: passed in via `useRealHistoricalPrices`.
  //
  // Engine: `reconstructBotEquityCurve` (equity-reconstruction.ts) —
  // same day-by-day simulation used at the account level.
  //
  // **Inception date selection.** `statusDate` from /bots/{id} is
  // sometimes a re-activation timestamp, not the bot's true birth
  // (verified Live S Martingale: statusDate=2025-05-28 but trading
  // history goes back to 2024-04-04). We anchor the inception to
  // `min(statusDate, firstOrderDate)` so the reconstruction sees
  // every fill the bot ever made. Anything earlier than the first
  // fill is a flat allocated-cash line — harmless.
  if (
    detail.portfolio &&
    typeof detail.portfolio.allocatedAmount === "number" &&
    detail.portfolio.allocatedAmount > 0
  ) {
    let earliestOrderDate: string | null = null;
    for (const o of orders) {
      const d = o.submittedAt ? o.submittedAt.slice(0, 10) : "";
      if (!d) continue;
      if (earliestOrderDate === null || d < earliestOrderDate) {
        earliestOrderDate = d;
      }
    }
    const statusISO =
      typeof detail.statusDate === "string" && detail.statusDate.length >= 10
        ? detail.statusDate.slice(0, 10)
        : null;
    const inceptionISO =
      statusISO && earliestOrderDate
        ? statusISO < earliestOrderDate
          ? statusISO
          : earliestOrderDate
        : statusISO ?? earliestOrderDate;

    if (inceptionISO) {
      const recon = reconstructBotEquityCurve({
        allocatedAmount: detail.portfolio.allocatedAmount,
        inceptionDate: inceptionISO,
        placedOrders: orders,
        prices,
        splits,
      });

      if (Number.isFinite(recon.maxDDFraction)) {
        out.maxDDFraction = recon.maxDDFraction;
      }
      if (typeof recon.sharpe === "number" && Number.isFinite(recon.sharpe)) {
        out.sharpe = recon.sharpe;
      }
      if (recon.sparkline.length > 0) {
        out.sparkline = recon.sparkline;
      }
      // Expose the full reconstructed curve so the bot drawer can
      // offer YTD / 1Y / ALL ranges, not just the 60-day sparkline.
      // Each entry is {date, equity}; the drawer slices by range and
      // hands the equity values to <Sparkline> with allocation as
      // the underwater baseline.
      if (recon.days.length > 0) {
        out.fullCurve = recon.days.map((d) => ({
          date: d.date,
          equity: d.equity
        }));
      }
      out.chartBaseline = detail.portfolio.allocatedAmount;

      // Diagnostic — stash on window so the console can inspect
      // per-bot reconstruction state (peak/trough/missing tickers)
      // without us hard-coding console.log spam. Mirrors the
      // account-level `window.__whbReconRuns` pattern.
      if (typeof window !== "undefined") {
        const w = window as unknown as {
          __whbBotRecon?: Record<string, unknown>;
        };
        w.__whbBotRecon ??= {};
        const days = recon.days;
        const lastEq = days[days.length - 1]?.equity ?? null;
        const peakEq = days.reduce(
          (m, d) => (d.equity > m ? d.equity : m),
          days[0]?.equity ?? 0
        );
        const peakIdx = days.findIndex((d) => d.equity === peakEq);
        const peakDate = days[peakIdx]?.date ?? null;
        let troughIdx = peakIdx;
        let troughEq = peakEq;
        for (let i = peakIdx; i < days.length; i++) {
          if (days[i]!.equity < troughEq) {
            troughEq = days[i]!.equity;
            troughIdx = i;
          }
        }
        w.__whbBotRecon[detail.id ?? inceptionISO] = {
          name: typeof (detail as { name?: string }).name === "string"
            ? (detail as { name?: string }).name
            : null,
          inceptionDate: inceptionISO,
          allocated: detail.portfolio.allocatedAmount,
          orderCount: orders.length,
          buyCount: orders.filter((o) => o.side === "buy").length,
          sellCount: orders.filter((o) => o.side === "sell").length,
          tickerCount: recon.tickersWithQuotes + recon.tickersMissingQuotes.length,
          tickersWithQuotes: recon.tickersWithQuotes,
          tickersMissingQuotes: recon.tickersMissingQuotes,
          dayCount: days.length,
          firstDate: days[0]?.date ?? null,
          lastDate: days[days.length - 1]?.date ?? null,
          peakEquity: peakEq,
          peakDate,
          troughEquity: troughEq,
          troughDate: days[troughIdx]?.date ?? null,
          finalEquity: lastEq,
          maxDDFraction: recon.maxDDFraction,
          sharpe: recon.sharpe,
        };
      }
    }
  }

  // Tickers — current holdings from /bots/{id}.stocks[]. Real bots
  // have empty tickers from /bots; lifting these in keeps the
  // drawer's "Universe" row identical between demo and real.
  if (Array.isArray(detail.stocks)) {
    const syms = new Set<string>();
    for (const s of detail.stocks) {
      if (s && typeof s.symbol === "string" && s.symbol) {
        syms.add(s.symbol);
      }
    }
    if (syms.size > 0) out.tickers = [...syms].sort();
  }

  // APY — annualise totalReturnFraction over the bot's age. Fall
  // back gracefully when statusDate isn't a valid date.
  if (
    typeof out.totalReturnFraction === "number" &&
    typeof detail.statusDate === "string"
  ) {
    const created = Date.parse(detail.statusDate);
    if (Number.isFinite(created)) {
      const ageYears = (Date.now() - created) / (365.25 * 86_400_000);
      if (ageYears > 0.05) {
        const total = 1 + out.totalReturnFraction;
        if (total > 0) {
          out.apyFraction = Math.pow(total, 1 / ageYears) - 1;
        }
      }
    }
  }

  return out;
}

/**
 * Per-session running tally of which provider (Massive / FMP /
 * Yahoo / cache / none) served each ticker. Populated by
 * `useRealHistoricalPrices` + the bulk details hook as responses
 * arrive; read by the reconstruction diagnostic log in
 * `whb-accounts-adapter` to report source breakdown per account.
 *
 * Kept as a module-scope mutable object on purpose — the diagnostic
 * is a debugging affordance, not a reactive display, and threading
 * it through React props for every ticker adds noise without payoff.
 */
export const quoteSourceRegistry: Record<string, string> = {};
// Mirror to `globalThis` so diagnostic code in neighbouring modules
// can read the registry without importing it directly (avoids a
// circular import through the adapter). Intentionally untyped: the
// consumer casts when reading.
if (typeof globalThis !== "undefined") {
  (globalThis as { __whbQuoteSources?: Record<string, string> }).__whbQuoteSources =
    quoteSourceRegistry;
}

/**
 * Daily SPY price point, normalised to the shape the risk helper
 * expects: one row per trading day, price-only close plus the
 * split+dividend-adjusted close for the SPY+Dividends line. Date is
 * ISO `YYYY-MM-DD`. Matches the JSON the Pages function
 * `functions/api/market/spy.ts` returns.
 */
export interface SpyPricePoint {
  date: string;
  close: number;
  adjClose: number;
}

interface QuotePoint {
  date: string;
  close: number;
  adjClose: number;
}

interface QuotesResponse {
  quotes: Record<string, QuotePoint[]>;
  splits?: Record<string, TickerSplit[]>;
  /** Per-ticker provider label: "massive" | "fmp" | "yahoo" |
   *  "cache:<orig>" | "none". Surfaced in the reconstruction console
   *  log so we can see at a glance which waterfall stage served each
   *  ticker. */
  sources?: Record<string, string>;
  missing?: string[];
  fetchedAt?: string;
}

interface SpyResponseBody {
  prices: SpyPricePoint[];
}

/**
 * Fetch daily closing prices for a set of tickers from
 * `/api/market/quotes`.
 *
 * Designed to back the equity-reconstruction engine, which needs a
 * close per ticker per day to mark historical positions to market.
 * Tickers are passed as a set (order-insensitive); the hook
 * deduplicates and sorts for a stable cache key so identical sets
 * across renders share one network request.
 *
 * Per-ticker results are cached in IndexedDB under
 * `cacheKey.tickerQuotes(ticker)` with a 24-hour freshness window
 * (prices update at most once per trading day). Fresh-cache-hit
 * tickers skip the network entirely; the hook only fires a request
 * when at least one ticker is stale or missing.
 *
 * Runs under both real and demo sessions — the quotes endpoint is
 * public upstream data, no auth required.
 */
export function useRealHistoricalPrices(tickers: readonly string[]): {
  prices: Record<string, QuotePoint[]>;
  splits: Record<string, TickerSplit[]>;
  /** Long company / ETF names per ticker, sourced from the same
   *  `/api/market/quotes` response. Empty for tickers Yahoo / FMP /
   *  Massive didn't recognise — caller should fall back to its own
   *  static lookup table when an entry is missing. */
  names: Record<string, string>;
  loading: boolean;
  error: string | null;
} {
  // Stable key — sorted-unique-joined so unordered duplicates across
  // re-renders share a cache entry and don't re-trigger fetches.
  const unique = useMemo(() => {
    const seen = new Set<string>();
    for (const t of tickers) {
      if (typeof t === "string" && t.length > 0) seen.add(t.toUpperCase());
    }
    return Array.from(seen).sort();
  }, [tickers]);
  const key = unique.join(",");

  const [state, setState] = useState<{
    prices: Record<string, QuotePoint[]>;
    splits: Record<string, TickerSplit[]>;
    names: Record<string, string>;
    loading: boolean;
    error: string | null;
  }>({ prices: {}, splits: {}, names: {}, loading: unique.length > 0, error: null });

  useEffect(() => {
    if (unique.length === 0) {
      setState({ prices: {}, splits: {}, names: {}, loading: false, error: null });
      return;
    }
    let cancelled = false;
    const MAX_AGE_MS = 24 * 60 * 60 * 1000;

    (async () => {
      // 1. Hydrate from IndexedDB per-ticker. Any fresh entry skips
      //    the network. Stale or missing entries go into the refetch
      //    set. Cache entries store {points, splits, name} so the
      //    engine's split-handling inputs and the UI's company-name
      //    lookups all paint instantly from one cache hit.
      const stale: string[] = [];
      const hydratedPrices: Record<string, QuotePoint[]> = {};
      const hydratedSplits: Record<string, TickerSplit[]> = {};
      const hydratedNames: Record<string, string> = {};
      await Promise.all(
        unique.map(async (t) => {
          const hit = await cacheGet<{
            points: QuotePoint[];
            splits?: TickerSplit[];
            name?: string;
          }>(cacheKey.tickerQuotes(t));
          const fresh = hit && Date.now() - hit.updatedAt < MAX_AGE_MS;
          // Old cache entries from before the split-aware schema were
          // plain `QuotePoint[]`. Sniff that and treat them as stale
          // so we refetch and get splits alongside.
          const hasSchemaV2 =
            hit && hit.value && typeof hit.value === "object" && !Array.isArray(hit.value);
          // Schema v3 introduced `name` (Sergey 2026-04-26). Cache
          // entries from v2 — written before names were piggybacked
          // on the quotes endpoint — are still "fresh" by the 24h TTL
          // but lack the name. Treat them as stale so the next request
          // re-resolves and persists the missing field. Without this,
          // accounts whose tickers were cached before today would
          // never get names until the natural 24h expiry.
          const hasName = hit?.value?.name && hit.value.name.length > 0;
          if (fresh && hasSchemaV2 && hasName) {
            hydratedPrices[t] = hit.value.points;
            if (hit.value.splits) hydratedSplits[t] = hit.value.splits;
            if (hit.value.name) hydratedNames[t] = hit.value.name;
          } else {
            stale.push(t);
            if (hasSchemaV2) {
              hydratedPrices[t] = hit.value.points; // paint stale
              if (hit.value.splits) hydratedSplits[t] = hit.value.splits;
              if (hit.value.name) hydratedNames[t] = hit.value.name;
            }
          }
        })
      );
      if (cancelled) return;
      if (Object.keys(hydratedPrices).length > 0) {
        setState({
          prices: hydratedPrices,
          splits: hydratedSplits,
          names: hydratedNames,
          loading: stale.length > 0,
          error: null,
        });
      }

      if (stale.length === 0) {
        if (!cancelled) {
          setState((s) => ({ ...s, loading: false, error: null }));
        }
        return;
      }

      // 2. Fetch the stale subset. The server caps at 10 tickers per
      // request. Cloudflare Workers Free plan allows 50 subrequests
      // per invocation; each ticker can fire up to 3 outbound fetches
      // (Massive → FMP → Yahoo), so 10 × 3 = 30 fits comfortably with
      // headroom for KV quota writes. When the project moves to
      // Workers Paid (1000 subrequest ceiling), bump this in concert
      // with MAX_TICKERS in functions/api/market/quotes.ts.
      const BATCH_SIZE = 10;
      const batches: string[][] = [];
      for (let i = 0; i < stale.length; i += BATCH_SIZE) {
        batches.push(stale.slice(i, i + BATCH_SIZE));
      }
      try {
        const results = await Promise.all(
          batches.map(async (group) => {
            const res = await fetch(
              `/api/market/quotes?tickers=${encodeURIComponent(group.join(","))}`
            );
            if (!res.ok) {
              // Log but don't throw — the Promise.all below tolerates
              // partial failure. Previously we threw and the entire
              // reconstruction fell through to cost-basis for the
              // entire batch's tickers. Returning an empty map keeps
              // the client functional; the server's handler-level
              // try/catch should already convert exceptions to 200s.
              console.warn(
                `[whb-data] /api/market/quotes ${res.status} for [${group.join(",")}]`
              );
              return { quotes: {}, splits: {}, names: {}, sources: {} };
            }
            const body = (await res.json()) as QuotesResponse & {
              error?: string;
              sources?: Record<string, string>;
              names?: Record<string, string>;
            };
            if (body.error) {
              console.warn(
                `[whb-data] /api/market/quotes returned error for [${group.join(",")}]: ${body.error}`
              );
            }
            return {
              quotes: body.quotes ?? {},
              splits: body.splits ?? {},
              names: body.names ?? {},
              sources: body.sources ?? {},
            };
          })
        );
        const nextPrices: Record<string, QuotePoint[]> = { ...hydratedPrices };
        const nextSplits: Record<string, TickerSplit[]> = { ...hydratedSplits };
        const nextNames: Record<string, string> = { ...hydratedNames };
        for (const batch of results) {
          const { quotes: quoteMap, splits: splitMap } = batch;
          // Stash the server's per-ticker source label into the
          // module-global registry so the reconstruction diagnostic
          // log can break down by provider later.
          const sourceMap = (batch as { sources?: Record<string, string> }).sources ?? {};
          const nameMap = (batch as { names?: Record<string, string> }).names ?? {};
          for (const t of Object.keys(quoteMap)) {
            const pts = quoteMap[t];
            if (Array.isArray(pts) && pts.length > 0) {
              nextPrices[t] = pts;
              const splits = Array.isArray(splitMap[t]) ? splitMap[t] : [];
              if (splits.length > 0) nextSplits[t] = splits;
              const nm = nameMap[t];
              if (typeof nm === "string" && nm.length > 0) {
                nextNames[t] = nm;
              }
              if (sourceMap[t]) quoteSourceRegistry[t] = sourceMap[t];
              // Persist combined {points, splits, name} so the next
              // render hydrates all three fields from one cache hit.
              void cacheSet(cacheKey.tickerQuotes(t), {
                points: pts,
                splits,
                ...(nextNames[t] ? { name: nextNames[t] } : {})
              });
            }
          }
        }
        if (cancelled) return;
        setState({
          prices: nextPrices,
          splits: nextSplits,
          names: nextNames,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          prices: s.prices, // keep whatever we painted from cache
          splits: s.splits,
          names: s.names,
          loading: false,
          error: (err as Error).message ?? String(err),
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

/**
 * Fetch SPY daily history from `/api/market/spy`.
 *
 * Unlike the account-scoped hooks this one runs **regardless of
 * session kind** — the comparison line is useful on mock/demo too,
 * and the endpoint is public upstream data (no auth). Cached for
 * 12 hours in IndexedDB so the chart has an instant-paint source on
 * reload without hammering the Cloudflare function, which itself
 * caches at the edge for an hour.
 *
 * Failures are soft: on any fetch error the hook returns whatever is
 * in the cache (possibly stale). Callers that get `null` should fall
 * back to the synthesized SPY walk in `buildRiskDataForTWR`.
 */
export function useRealSpyPriceSeries(): {
  prices: SpyPricePoint[] | null;
  loading: boolean;
  error: string | null;
} {
  const [state, setState] = useState<{
    prices: SpyPricePoint[] | null;
    loading: boolean;
    error: string | null;
  }>({ prices: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    const key = cacheKey.spySeries();
    const MAX_AGE_MS = 12 * 60 * 60 * 1000;

    (async () => {
      // Cache-first paint.
      const hit = await cacheGet<SpyResponseBody>(key);
      if (!cancelled && hit?.value?.prices) {
        setState({ prices: hit.value.prices, loading: true, error: null });
      }
      // Skip the network call entirely when the cache is fresh — SPY
      // only prints once per trading day and burning the CF function
      // on every refresh is wasteful.
      if (hit && Date.now() - hit.updatedAt < MAX_AGE_MS) {
        if (!cancelled) {
          setState({ prices: hit.value.prices, loading: false, error: null });
        }
        return;
      }

      try {
        const res = await fetch("/api/market/spy");
        if (!res.ok) throw new Error(`/api/market/spy ${res.status}`);
        const body = (await res.json()) as SpyResponseBody;
        if (!Array.isArray(body.prices) || body.prices.length === 0) {
          throw new Error("empty spy series");
        }
        void cacheSet(key, body);
        if (cancelled) return;
        setState({ prices: body.prices, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        // Keep whatever we painted from cache; only surface the error
        // when there was nothing to show.
        setState((s) => ({
          prices: s.prices,
          loading: false,
          error: (err as Error).message ?? String(err),
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * Resolve the PWA `Bot[]` attached to a given account.
 *
 * For real sessions: pulls the cached `/bots` list and filters by
 * `account.botIds` (which is populated by the detail adapter). Each
 * matched bot gets its `accountId` stamped from the account being
 * viewed — the list endpoint doesn't carry that field, so we backfill
 * here so downstream filters ("bots for this account") keep working.
 *
 * Returns `null` while the real bots list is still loading for the
 * first time; returns an empty array for demo sessions (callers fall
 * back to the mock bot set). Paper accounts under a real session also
 * receive a mapped-but-potentially-empty list — the hook doesn't
 * distinguish live from paper; `account.botIds` is the authority.
 */
export function useRealBotsForAccount(
  account: BrokerAccount | null
): Bot[] | null {
  const { bots } = useRealBots();
  return useMemo<Bot[] | null>(() => {
    if (!account || account.source !== "real") return null;
    if (bots === null) return null;
    const wanted = new Set(account.botIds);
    return bots
      .filter((b) => wanted.has(b.id))
      .map((b) => ({ ...b, accountId: account.id }));
  }, [bots, account]);
}

/** Retention window for archived bots' cached data. After this many
 *  days since `lastSeenIso`, the bot is purged from the registry +
 *  bot-detail/bot-orders caches and disappears from the Archived
 *  view. Sergey 2026-04-30: "we will keep bots for max 6 months". */
export const ARCHIVED_BOT_MAX_AGE_DAYS = 180;

/**
 * Archived bots (deleted from the platform) for the given account ids.
 * Reads the persistent bot registry (`bot-registry:root`) + per-bot
 * detail/orders caches; returns synthetic `Bot` records with the
 * last-known portfolio state, sorted newest-archived first.
 *
 * An archived bot is one whose registry entry's `lastSeenIso` is NOT
 * today (i.e. it didn't appear in the current /bots fetch) AND whose
 * `accountId` is in the requested set. Falls within the 6-month
 * retention window — older entries are purged here as a side effect.
 *
 * Returns `[]` while the registry is empty OR the live `/bots` fetch
 * hasn't resolved yet (we wait for `bots` to be non-null so we can
 * subtract active ids correctly). Demo / signed-out sessions return
 * `[]` unconditionally — archived list is real-session only.
 */
/** Subtract one calendar day from an ISO yyyy-mm-dd date, returning
 *  the same format. Used to derive the last-day-with-positions date
 *  from a detected liquidation timestamp. */
function prevDayIso(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * One user-supplied allocation event for an archived bot — a deposit
 * (positive deltaUsd) or withdrawal (negative) that the user remembers
 * setting on a specific date. Sergey 2026-04-30: bot 070403cf was
 * reduced from ~$23,300 to $18,980 on 2026-02-18, so the events list
 * for that bot would be `[{ date: "2026-02-18", deltaUsd: -4320 }]`.
 */
export interface AllocationEvent {
  /** ISO yyyy-mm-dd. */
  date: string;
  /** Signed dollar amount. Positive = bumped allocation (deposit),
   *  negative = reduced allocation (withdrawal). */
  deltaUsd: number;
}

export interface ArchivedBotOverride {
  events: AllocationEvent[];
}

/**
 * One day of an archived bot's reconstructed history. `equity` is the
 * dollar BPV (cash + market value of held positions); `twrPct` is the
 * cumulative time-weighted return at that point as a fraction. The
 * drawer plots `twrPct` as a percentage line — cashflows like
 * allocation reductions don't appear as drops because TWR isolates
 * them. Sergey 2026-04-30: archived bot lifetime chart shows TWR%,
 * not $.
 */
export interface ArchivedBotDay {
  date: string;
  equity: number;
  twrPct: number;
}

export function useArchivedBots(accountIds: readonly string[]): {
  bots: Bot[];
  curves: Record<string, ArchivedBotDay[]>;
  /** User-supplied allocation event overrides per bot id. Empty
   *  object until IDB load completes. The drawer reads this to render
   *  the allocation-events list and calls `setOverride` to persist
   *  edits — that flips a version counter which re-runs the
   *  reconstruction stage with the new events. */
  overrides: Record<string, ArchivedBotOverride>;
  setOverride: (botId: string, ovr: ArchivedBotOverride) => void;
} {
  const kind = useAuthStore((s) => s.kind);
  const { bots: activeBots } = useRealBots();
  const accountKey = accountIds.join(",");
  // Stage 1 state — simple bots derived from cache scans, plus the
  // raw trades per bot so the reconstruction stage downstream can
  // run without re-walking the cache.
  const [stage1, setStage1] = useState<{
    simpleBots: Bot[];
    tradesById: Record<string, readonly RawTradingHistoryRow[]>;
  }>({ simpleBots: [], tradesById: {} });

  useEffect(() => {
    if (kind !== "real" || !activeBots) {
      setStage1({ simpleBots: [], tradesById: {} });
      return;
    }
    let cancelled = false;
    void (async () => {
      const reg =
        (await cacheGet<Record<string, BotRegistryEntry>>(cacheKey.botRegistry()))?.value ?? {};
      const accountSet = new Set(accountIds);
      const activeIds = new Set(activeBots.map((b) => b.id));
      const cutoffMs = Date.now() - ARCHIVED_BOT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

      // Walk every cached `bot-detail:{id}` entry directly — this is the
      // source of truth for "we have data on this bot". The registry is
      // a side index for friendly names + lastSeenIso, but it only
      // covers bots seen by /bots after the archived-bots feature
      // shipped. Pre-feature deletions (e.g. Default Live's bot,
      // deleted Apr 28 before the registry was first written) have no
      // registry entry but DO have a cached detail blob from when the
      // user opened the bot's drawer. Scanning the cache prefix
      // recovers those. Sergey 2026-04-30.
      const detailRows = await cacheListByPrefix<RawBotDetail>("bot-detail:");
      const stale: string[] = [];
      const candidates: Array<{
        id: string;
        name: string;
        accountId: string;
        lastSeenIso: string;
        detail: RawBotDetail;
      }> = [];
      for (const row of detailRows) {
        const id = row.key.slice("bot-detail:".length);
        if (!id || activeIds.has(id)) continue;
        const detail = row.value;
        if (!detail) continue;

        // Account scope — registry's accountId wins (was set during a
        // live /bots/{id} fetch via useRealBotDetailsBulk), else fall
        // back to the cached detail's brokerAccountId. If neither
        // surfaces a match for the requested accountIds, skip.
        const regEntry = reg[id];
        const accountId =
          (regEntry?.accountId) ||
          (typeof detail.brokerAccountId === "string" ? detail.brokerAccountId : "");
        if (accountSet.size > 0 && (!accountId || !accountSet.has(accountId))) continue;

        // Retention — registry's lastSeenIso wins (set on every /bots
        // refresh), else the cache row's updatedAt timestamp (when the
        // bot detail was last written, i.e. last time the bot was
        // viewed/active).
        const lastSeenIso =
          (regEntry?.lastSeenIso) ||
          new Date(row.updatedAt).toISOString().slice(0, 10);
        const seenMs = new Date(lastSeenIso).getTime();
        if (!Number.isFinite(seenMs) || seenMs < cutoffMs) {
          stale.push(id);
          continue;
        }

        candidates.push({
          id,
          name: regEntry?.name || `Bot · ${id.slice(0, 8)}`,
          accountId,
          lastSeenIso,
          detail
        });
      }

      // Lazy purge — drop registry entries + their cached detail/orders
      // for bots beyond the 6-month retention. Best-effort; failures
      // here don't block the read path.
      if (stale.length > 0) {
        const next = { ...reg };
        for (const id of stale) {
          delete next[id];
          void cacheDelete(cacheKey.botDetail(id));
          void cacheDelete(cacheKey.botOrders(id));
        }
        await cacheSet(cacheKey.botRegistry(), next);
      }

      // Synthesise a Bot row per detail-cache candidate from its frozen
      // detail blob. Track which ids we've already covered so the
      // trading-history pass below doesn't double-add them.
      const synthesized: Bot[] = candidates.map((c) =>
        buildArchivedBotFromDetail(c.id, c.name, c.accountId, c.lastSeenIso, c.detail)
      );
      const coveredIds = new Set(candidates.map((c) => c.id));
      // Per-bot raw trades — we'll feed these into the reconstruction
      // stage below. For detail-cache bots we don't have a full
      // trading history at this point (the detail blob's
      // `placedOrders` is partial), so they go in empty; the row
      // still renders from the detail-derived metrics.
      const tradesById: Record<string, RawTradingHistoryRow[]> = {};
      for (const id of coveredIds) tradesById[id] = [];

      // Trading-history fallback (Sergey 2026-04-30): if a bot was
      // deleted before any local cache shipped — no registry entry,
      // no bot-detail blob — its trades are still in the per-account
      // trading-history cache (`orders:{accountId}`). Enumerate
      // distinct botIds from each in-scope account's history,
      // subtract active/already-covered ids, and synthesise a sparse
      // archived Bot from the trades alone (tickers from distinct
      // symbols, lastSeenIso from latest trade date, no portfolio).
      // This recovers the Default Live deletion that pre-dates the
      // archived-bots feature.
      const ordersScope =
        accountSet.size > 0
          ? Array.from(accountSet).map((aid) => ({
              key: cacheKey.orders(aid),
              accountId: aid
            }))
          : [];
      const orderHits = await Promise.all(
        ordersScope.map(async (s) => ({
          accountId: s.accountId,
          hit: await cacheGet<RawTradingHistoryResponse | RawTradingHistoryRow[]>(s.key)
        }))
      );
      const tradesByBotId = new Map<
        string,
        { accountId: string; rows: RawTradingHistoryRow[]; lastDateIso: string }
      >();
      for (const { accountId, hit } of orderHits) {
        if (!hit) continue;
        const raw = hit.value;
        const records: RawTradingHistoryRow[] = Array.isArray(raw)
          ? (raw as RawTradingHistoryRow[])
          : Array.isArray((raw as RawTradingHistoryResponse | undefined)?.records)
            ? (raw as RawTradingHistoryResponse).records!
            : [];
        for (const r of records) {
          if (!r) continue;
          const bid = typeof r.botId === "string" ? r.botId : null;
          if (!bid) continue;
          if (activeIds.has(bid) || coveredIds.has(bid)) continue;
          // Per-account scope was enforced by which keys we read,
          // but defensively re-check the row's brokerAccountId so
          // a misfiled cache row can't leak across accounts.
          const rowAccount =
            typeof r.brokerAccountId === "string" ? r.brokerAccountId : accountId;
          if (accountSet.size > 0 && !accountSet.has(rowAccount)) continue;
          const existing = tradesByBotId.get(bid);
          const dateIso = typeof r.date === "string" ? r.date.slice(0, 10) : "";
          if (!existing) {
            tradesByBotId.set(bid, {
              accountId: rowAccount,
              rows: [r],
              lastDateIso: dateIso
            });
          } else {
            existing.rows.push(r);
            if (dateIso && dateIso > existing.lastDateIso) {
              existing.lastDateIso = dateIso;
            }
          }
        }
      }

      // Per-account liquidation detection (Sergey 2026-04-30): when a
      // bot is deleted, the broker liquidates its open positions via
      // direct Alpaca fills (bypassing the WHB bot pipeline). Those
      // sells land in the missed-orders feed with both
      // `tradingHistoryRecordId == null` (no bot link) AND
      // `botName == null` (orphan attribution). For each in-scope
      // account, the latest such date is the liquidation day; the
      // bot's "frozen" date is the trading day before that — the
      // last day the bot still held positions and the last point on
      // its lifetime chart.
      const liquidationDateByAccount = new Map<string, string>();
      const missedHits = await Promise.all(
        ordersScope.map(async (s) => ({
          accountId: s.accountId,
          hit: await cacheGet<unknown>(cacheKey.missedOrders(s.accountId))
        }))
      );
      for (const { accountId, hit } of missedHits) {
        if (!hit) continue;
        const raw = (hit as { value: unknown }).value;
        if (!Array.isArray(raw)) continue;
        let maxDate = "";
        for (const r of raw as Array<{
          tradingHistoryRecordId?: string | null;
          botName?: string | null;
          date?: string;
          action?: number;
        }>) {
          if (!r) continue;
          if (r.tradingHistoryRecordId) continue;
          if (r.botName) continue;
          // sells only — buys after a bot deletion would be unusual
          // and shouldn't drive the liquidation timestamp
          if (r.action !== 2) continue;
          const d = typeof r.date === "string" ? r.date.slice(0, 10) : "";
          if (d && d > maxDate) maxDate = d;
        }
        if (maxDate) liquidationDateByAccount.set(accountId, maxDate);
      }

      const todayIso = new Date().toISOString().slice(0, 10);
      for (const [botId, bundle] of tradesByBotId) {
        const lastSeenIso = bundle.lastDateIso || todayIso;
        const seenMs = new Date(lastSeenIso).getTime();
        if (!Number.isFinite(seenMs) || seenMs < cutoffMs) continue;
        const regEntry = reg[botId];
        const name = regEntry?.name || `Bot · ${botId.slice(0, 8)}`;
        // If liquidation happened AFTER the bot's last trade, use
        // (liquidation − 1 day) as the archived date — the last day
        // the bot still held positions.
        const acctLiq = liquidationDateByAccount.get(bundle.accountId);
        const archivedAt =
          acctLiq && acctLiq > lastSeenIso ? prevDayIso(acctLiq) : lastSeenIso;
        synthesized.push(
          buildArchivedBotFromTrades(
            botId,
            name,
            bundle.accountId,
            archivedAt,
            bundle.rows
          )
        );
        coveredIds.add(botId);
        // Record the raw trades for this bot — the reconstruction
        // stage below will run them through the equity engine when
        // historical prices are available.
        tradesById[botId] = bundle.rows;
      }
      if (cancelled) return;
      synthesized.sort((a, b) =>
        (b.archivedAt ?? "").localeCompare(a.archivedAt ?? "")
      );
      setStage1({ simpleBots: synthesized, tradesById });
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, activeBots, accountKey, accountIds]);

  // Stage 2: union of tickers across all archived bots, used to
  // hydrate / fetch historical prices once. `useRealHistoricalPrices`
  // reads its own per-ticker IndexedDB cache first, so for any bot
  // whose tickers were ever fetched (active period), prices paint
  // instantly from disk and reconstruction runs without any network.
  const allTickers = useMemo(() => {
    const set = new Set<string>();
    for (const bot of stage1.simpleBots) {
      for (const t of bot.tickers) set.add(t);
    }
    return Array.from(set);
  }, [stage1.simpleBots]);
  const { prices, splits } = useRealHistoricalPrices(allTickers);

  // Per-bot allocation override events, loaded from IndexedDB. Keyed
  // by bot id. When the user adds / edits an event in the drawer,
  // `setOverride` writes to IDB and bumps `overridesVersion` to
  // re-trigger the reconstruction stage below.
  const [overrides, setOverridesState] = useState<
    Record<string, ArchivedBotOverride>
  >({});
  const [overridesVersion, setOverridesVersion] = useState(0);
  // Load overrides for all currently-known archived bot ids whenever
  // the simpleBots set changes. Re-loads when overridesVersion bumps
  // (after a setOverride write) so the new events flow into recon.
  useEffect(() => {
    const ids = stage1.simpleBots.map((b) => b.id);
    if (ids.length === 0) {
      setOverridesState({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const next: Record<string, ArchivedBotOverride> = {};
      await Promise.all(
        ids.map(async (id) => {
          const hit = await cacheGet<ArchivedBotOverride>(
            cacheKey.archivedBotOverrides(id)
          );
          if (hit?.value) next[id] = hit.value;
        })
      );
      if (!cancelled) setOverridesState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [stage1.simpleBots, overridesVersion]);
  const setOverride = useCallback(
    (botId: string, ovr: ArchivedBotOverride) => {
      void cacheSet(cacheKey.archivedBotOverrides(botId), ovr).then(() => {
        setOverridesState((prev) => ({ ...prev, [botId]: ovr }));
        setOverridesVersion((v) => v + 1);
      });
    },
    []
  );

  // Stage 3: per-bot reconstruction. For each archived bot with
  // trades, run the same equity engine the active-bot drawer uses,
  // then merge the resulting metrics + curve into the simple bot
  // record. Bots without trades (detail-only candidates whose trade
  // history isn't available) keep their detail-derived numbers.
  // User-supplied allocation events from `overrides` are passed in as
  // additional cashflows so the curve reflects mid-stream ND changes.
  const reconResult = useMemo(() => {
    const bots: Bot[] = [];
    const curves: Record<string, ArchivedBotDay[]> = {};
    for (const simple of stage1.simpleBots) {
      const trades = stage1.tradesById[simple.id] ?? [];
      const ovr = overrides[simple.id];
      const additionalCashflows: Cashflow[] | undefined = ovr?.events?.length
        ? ovr.events.map((e) => ({
            date: e.date,
            amount: e.deltaUsd,
            type:
              e.deltaUsd >= 0
                ? ("deposit" as const)
                : ("withdrawal" as const)
          }))
        : undefined;
      const result = enrichArchivedBotWithRecon(
        simple,
        trades,
        prices,
        splits,
        additionalCashflows
      );
      bots.push(result.bot);
      if (result.days.length > 0) curves[simple.id] = result.days;
    }
    return { bots, curves };
  }, [stage1, prices, splits, overrides]);

  return {
    bots: reconResult.bots,
    curves: reconResult.curves,
    overrides,
    setOverride
  };
}

/**
 * Run `reconstructBotEquityCurve` over an archived bot's cached
 * trades and merge the resulting metrics into the bot record. Falls
 * back to the simple bot (zero metrics) when inputs are insufficient
 * — no allocation, no inception date, or no orders. Sergey 2026-04-30.
 */
function enrichArchivedBotWithRecon(
  simple: Bot,
  trades: readonly RawTradingHistoryRow[],
  prices: HistoricalQuotesMap,
  splits: HistoricalSplitsMap,
  additionalCashflows?: readonly Cashflow[]
): { bot: Bot; days: ArchivedBotDay[] } {
  if (
    !simple.allocatedAmount ||
    simple.allocatedAmount <= 0 ||
    !simple.createdAt ||
    trades.length === 0
  ) {
    return { bot: simple, days: [] };
  }

  // Map raw trading-history rows → engine's `Order` shape. Failures
  // (no symbol / no qty / etc.) drop the row.
  const placedOrders: Order[] = [];
  for (const t of trades) {
    const o = mapRealTradeToOrder(t);
    if (o) placedOrders.push(o);
  }
  if (placedOrders.length === 0) return { bot: simple, days: [] };

  // Effective ND for return calculation — initial allocation plus
  // user-supplied allocation events (positive = bumped, negative =
  // reduced). Sergey 2026-04-30 (revised): WHB allocation changes
  // ARE cash transfers between the bot and account, so they belong
  // in the recon engine as cashflows. The $ equity curve drops on
  // withdrawal days; chain-linked TWR isolates them so the % return
  // line is unaffected by cashflow timing. Both views match the
  // user's mental model: $ shows the bot's actual capital trajectory,
  // % shows pure trade performance.
  const baseND =
    simple.allocatedAmount +
    (additionalCashflows ?? []).reduce((s, cf) => s + cf.amount, 0);

  const recon = reconstructBotEquityCurve({
    allocatedAmount: simple.allocatedAmount,
    inceptionDate: simple.createdAt,
    placedOrders,
    prices,
    splits,
    additionalCashflows
  });
  if (recon.days.length === 0) return { bot: simple, days: [] };

  // Truncate the lifetime curve at the bot's archived date — for an
  // archived bot we don't want to show mark-to-market days past the
  // moment the bot was deleted. The engine walks through `today` by
  // default; for active bots that's right, but for archived bots the
  // chart should freeze at the last day the bot still held positions
  // (one trading day before liquidation). archivedAt is set upstream
  // by the liquidation-detection logic in `useArchivedBots`.
  const truncatedDays = simple.archivedAt
    ? recon.days.filter((d) => d.date <= simple.archivedAt!)
    : recon.days;
  const daysForChart =
    truncatedDays.length > 0 ? truncatedDays : recon.days;

  // Chain-linked TWR — daily return r_t = (equity_t − cf_t) /
  // equity_{t−1}, with the inception deposit treated as the day-0
  // cashflow so r_0 = 0. Each cashflow on day t is removed from the
  // numerator before dividing, isolating trade-driven gains. The
  // engine's day-0 entry already INCLUDES the inception deposit
  // (post-cashflow opening), so we anchor the chain at index 0 and
  // walk forward.
  const cfByDay = new Map<string, number>();
  cfByDay.set(simple.createdAt.slice(0, 10), simple.allocatedAmount);
  for (const cf of additionalCashflows ?? []) {
    const k = cf.date.slice(0, 10);
    cfByDay.set(k, (cfByDay.get(k) ?? 0) + cf.amount);
  }
  let cumGrowth = 1;
  const archivedDays: ArchivedBotDay[] = [];
  let prevEquity = daysForChart[0]?.equity ?? 0;
  archivedDays.push({
    date: daysForChart[0]!.date,
    equity: daysForChart[0]!.equity,
    twrPct: 0
  });
  for (let i = 1; i < daysForChart.length; i++) {
    const dayT = daysForChart[i]!;
    const cfToday = cfByDay.get(dayT.date) ?? 0;
    if (prevEquity > 0) {
      const dailyGrowth = (dayT.equity - cfToday) / prevEquity;
      if (Number.isFinite(dailyGrowth) && dailyGrowth > 0) {
        cumGrowth *= dailyGrowth;
      }
    }
    archivedDays.push({
      date: dayT.date,
      equity: dayT.equity,
      twrPct: cumGrowth - 1
    });
    prevEquity = dayT.equity;
  }

  const last = daysForChart[daysForChart.length - 1]!;
  const finalEquity = last.equity;
  const effectiveND = baseND > 0 ? baseND : simple.allocatedAmount;
  const allTimeGain = finalEquity - effectiveND;
  const twrFraction = cumGrowth - 1;

  // APY — annualise chain-linked TWR over the bot's actual lifetime.
  // Below ~7 days the annualisation is unstable (tiny denominator →
  // wild swings), so we just echo the raw TWR.
  let apyFraction = twrFraction;
  const lifetimeDays =
    (new Date(last.date).getTime() - new Date(simple.createdAt).getTime()) /
    86400e3;
  if (lifetimeDays >= 7) {
    const years = lifetimeDays / 365;
    apyFraction = Math.pow(1 + twrFraction, 1 / years) - 1;
  }

  // Sparkline — last ~90 days from the truncated curve so the row
  // sparkline doesn't include post-deletion mark-to-market days.
  // Sparklines stay in $ (equity) since the row caller treats them
  // as equity — no consumer of the row reads twrPct from sparkline.
  const SPARK_LEN = 90;
  const sparkline = daysForChart
    .slice(-SPARK_LEN)
    .map((d) => d.equity)
    .filter((v) => Number.isFinite(v));

  return {
    bot: {
      ...simple,
      allocatedAmount: +effectiveND.toFixed(2),
      netDeposits: +effectiveND.toFixed(2),
      currentValue: +finalEquity.toFixed(2),
      allTimeGain: +allTimeGain.toFixed(2),
      // allTimeGainPct now uses chain-linked TWR — isolates the
      // cashflow effect, so a bot whose absolute return is small
      // relative to peak deployment doesn't get penalised in % terms
      // just because the user pulled allocation back.
      allTimeGainPct: twrFraction,
      performancePct: twrFraction,
      twrPct: twrFraction,
      apyPct: apyFraction,
      sharpe: typeof recon.sharpe === "number" ? recon.sharpe : 0,
      maxDDPct: recon.maxDDFraction,
      sparkline: sparkline.length > 0 ? sparkline : simple.sparkline
    },
    days: archivedDays
  };
}

/**
 * Build a synthetic `Bot` from a cached `/bots/{id}` detail blob.
 * Used by `useArchivedBots` to render frozen state for deleted bots —
 * the platform won't return the bot anymore, so this is the only path
 * to display its last-known portfolio + tickers + sparkline.
 *
 * Numeric fallback to 0 throughout — better to render zero than NaN.
 */
function buildArchivedBotFromDetail(
  botId: string,
  name: string,
  accountId: string,
  lastSeenIso: string,
  detail: RawBotDetail
): Bot {
  const portfolio = detail.portfolio ?? {};
  const equity =
    typeof portfolio.equity === "number" ? portfolio.equity : 0;
  const allocated =
    typeof portfolio.allocatedAmount === "number"
      ? portfolio.allocatedAmount
      : 0;
  const buyingPower =
    typeof portfolio.buyingPower === "number" ? portfolio.buyingPower : 0;
  const cash = typeof portfolio.cash === "number" ? portfolio.cash : 0;
  const totalProfit =
    typeof portfolio.totalProfit === "number"
      ? portfolio.totalProfit
      : equity - allocated;
  const totalProfitFraction =
    typeof portfolio.totalProfitPercent === "number"
      ? portfolio.totalProfitPercent / 100
      : allocated > 0
        ? totalProfit / allocated
        : 0;

  // Tickers — from /bots/{id}.stocks[]. Same source as active bots'
  // BotOverlay.tickers, so the drawer's "Universe" row reads the same.
  const tickers: string[] = Array.isArray(detail.stocks)
    ? detail.stocks
        .map((s) => (s && typeof s.symbol === "string" ? s.symbol : null))
        .filter((t): t is string => !!t)
    : [];

  // Sparkline — last ~90 daily snapshots of cash + stocks from
  // states[]. Sorted ascending. Mirrors the active-bot sparkline so
  // the row visualisation stays identical.
  const sparkline: number[] = (() => {
    if (!Array.isArray(detail.states)) return [];
    const series: Array<{ ts: number; v: number }> = [];
    for (const s of detail.states) {
      if (!s || typeof s.timestamp !== "string") continue;
      const ts = Date.parse(s.timestamp);
      if (!Number.isFinite(ts)) continue;
      const c = typeof s.cash === "number" ? s.cash : 0;
      const k = typeof s.stocks === "number" ? s.stocks : 0;
      series.push({ ts, v: c + k });
    }
    series.sort((a, b) => a.ts - b.ts);
    return series.slice(-90).map((p) => p.v);
  })();

  return {
    id: botId,
    name: name || `Bot · ${botId.slice(0, 8)}`,
    accountId,
    status: "PAUSED",
    allocatedAmount: allocated,
    currentValue: equity,
    performancePct: totalProfitFraction,
    lastTradeDate:
      typeof detail.statusDate === "string" ? detail.statusDate : lastSeenIso,
    sparkline,
    tickers,
    description: "",
    buyingPower,
    cash,
    allTimeGain: totalProfit,
    allTimeGainPct: totalProfitFraction,
    createdAt:
      typeof detail.statusDate === "string" && detail.statusDate.length >= 10
        ? detail.statusDate.slice(0, 10)
        : "",
    twrPct: totalProfitFraction,
    apyPct: 0,
    sharpe: 0,
    maxDDPct: 0,
    netDeposits: allocated,
    dayChangePct: 0,
    archived: true,
    archivedAt: lastSeenIso
  };
}

/**
 * Trades-only fallback synthesis (Sergey 2026-04-30). Used when a bot
 * was deleted before any caching infrastructure shipped, so neither
 * the registry nor `bot-detail:{id}` has anything for it — but the
 * trades remain in the per-account `/trading-history` cache. We can
 * still surface a row showing the symbols traded, last activity date,
 * and net realised cash (sells − buys). Portfolio metrics are zero
 * because we have no equity/allocation snapshot for a deleted bot.
 */
function buildArchivedBotFromTrades(
  botId: string,
  name: string,
  accountId: string,
  lastSeenIso: string,
  trades: readonly RawTradingHistoryRow[]
): Bot {
  // Sort ascending so cumulative walks reflect chronological order.
  // RawTradingHistoryRow.date is ISO8601 with timezone, lexical sort
  // works correctly.
  const sorted = [...trades]
    .filter((t) => t && typeof t.date === "string")
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));

  // Walk trades in time order to derive:
  //   - allocatedAmount: peak cumulative net cash deployed (max of
  //     running buys − sells balance). This is the most capital the
  //     bot ever required from the parent account, the natural "size"
  //     to anchor TWR/APY against.
  //   - inceptionDate / lastTradeDate: first and last trade dates.
  //   - dailyRealized: per-day running cumulative realised P&L
  //     (sells − buys), used as a sparkline approximation since we
  //     can't easily mark-to-market without prices in this fallback
  //     path. Step-function: flat between trades, jumps on each fill.
  const tickerSet = new Set<string>();
  let runningSpent = 0; // Σ buys − Σ sells so far
  let peakSpent = 0;   // max(runningSpent) over time
  let netRealised = 0; // Σ sells − Σ buys (final P&L if all positions liquidated at zero)
  let firstDateIso = "";
  let lastDateIso = "";
  const dailyRealized: Array<{ date: string; cum: number }> = [];
  for (const t of sorted) {
    if (typeof t.symbol === "string" && t.symbol) tickerSet.add(t.symbol);
    const dateIso = (t.date || "").slice(0, 10);
    if (dateIso) {
      if (!firstDateIso) firstDateIso = dateIso;
      lastDateIso = dateIso;
    }
    if (typeof t.total === "number" && Number.isFinite(t.total)) {
      // action: 1 = Buy (cash out), 2 = Sell (cash in).
      if (t.action === 1) {
        runningSpent += t.total;
        netRealised -= t.total;
      } else if (t.action === 2) {
        runningSpent -= t.total;
        netRealised += t.total;
      }
      if (runningSpent > peakSpent) peakSpent = runningSpent;
    }
    if (dateIso) dailyRealized.push({ date: dateIso, cum: netRealised });
  }

  // Net Deposits estimator (Sergey 2026-04-30, refined): bots have
  // no margin by design — the bot can never spend more than its
  // allocated amount at any moment. This means
  //   peakSpent(t) = max over t' ≤ t of (cum_buys − cum_sales)
  // is a HARD LOWER BOUND on ND at the time of peak: the bot couldn't
  // have hit that spending level without ND ≥ peakSpent. Since
  // there's no upper bound from data alone (the user could have
  // allocated more than was ever deployed), we use peakSpent as the
  // ND estimate. This is the tightest bound from orders alone.
  //
  // Limitations:
  //  - User-driven allocation reductions mid-stream (e.g. user
  //    pulled $4,320 back from bot 070403cf on 2026-02-18) are
  //    invisible here — we'd see the same peakSpent regardless of
  //    later cuts. Per-bot mid-stream allocation events come later
  //    via a UI override; for now ND reflects the bot's MAX historical
  //    allocation, which is the right value for "lifetime TWR" against
  //    the largest capital it ever managed.
  //  - "Bot Stock Portfolio Value − Realized − Unrealized" simplifies
  //    to peakSpent at any day with cash = 0; under no-margin, that
  //    cash-zero day is exactly when peakSpent peaked. So the formula
  //    and this estimator give the same number.
  const allocatedAmount = peakSpent > 0 ? peakSpent : 0;
  return {
    id: botId,
    name: name || `Bot · ${botId.slice(0, 8)}`,
    accountId,
    status: "PAUSED",
    allocatedAmount,
    currentValue: 0,
    performancePct: 0,
    lastTradeDate: lastDateIso || lastSeenIso,
    // Sparkline left empty — a step-function from cumulative realised
    // P&L would mislead the eye into thinking it's a real equity
    // curve. Better empty than wrong.
    sparkline: [],
    tickers: Array.from(tickerSet),
    description: "",
    buyingPower: 0,
    cash: 0,
    allTimeGain: 0,
    allTimeGainPct: 0,
    createdAt: firstDateIso,
    twrPct: 0,
    apyPct: 0,
    sharpe: 0,
    maxDDPct: 0,
    netDeposits: allocatedAmount,
    dayChangePct: 0,
    archived: true,
    archivedAt: lastDateIso || lastSeenIso
  };
}

/**
 * Same data as `useRealBrokerAccounts`, but adapted to the PWA's
 * internal `BrokerAccount` shape so the selector + hero can consume
 * real accounts without every call site knowing about the raw API.
 * Returned array is `null` until the first response (or if the user
 * isn't signed in with a real account).
 */
export function useRealAccountsAdapted(): {
  accounts: BrokerAccount[] | null;
  error: string | null;
  loading: boolean;
} {
  const { data, error, loading } = useRealBrokerAccounts<unknown>();
  // Client-side hide list — compensates for the backend returning
  // all accounts the user's role can see (admin users see other
  // users' accounts too). See `store/account-visibility-store.ts`
  // for the rationale and the ticket tracking a server-side fix.
  const hiddenIds = useAccountVisibilityStore((s) => s.hiddenIds);
  // Per-browser nickname overrides — applied here at the mapping
  // boundary so every downstream consumer (header, selector, drawers,
  // search) transparently sees the renamed value without each having
  // to know about the store. Sergey 2026-05-13: SettingsView writes
  // here when the user edits the nickname; the header and dropdown
  // pick up the change automatically since they read this list.
  const nicknameOverrides = useAccountNicknamesStore((s) => s.overrides);
  const accounts = useMemo<BrokerAccount[] | null>(() => {
    if (data === null) return null;
    const hiddenSet = new Set(hiddenIds);
    const mapped = mapRealBrokerAccounts(data).filter(
      (a) => !hiddenSet.has(a.id)
    );
    // Apply nickname overrides BEFORE sorting so the sort respects
    // the user-visible name (e.g. renamed "Z…" still sorts under Z,
    // not the original API name's position).
    const renamed = applyAccountNicknames(mapped, nicknameOverrides);
    // Keep a deterministic order: live before paper, then name asc.
    // Mirrors the mock selector's Live/Paper sectioning.
    return [...renamed].sort((a, b) => {
      if (a.paper !== b.paper) return a.paper ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  }, [data, hiddenIds, nicknameOverrides]);
  return { accounts, error, loading };
}

/**
 * Same as `useRealAccountsAdapted` but returns the **unfiltered** list
 * so the visibility UI itself can show hidden entries with their
 * "hidden" state rendered. The Profile page's "Manage account
 * visibility" dialog uses this hook so users can un-hide accounts
 * they've previously dropped from the selector.
 */
export function useRealAccountsUnfiltered(): {
  accounts: BrokerAccount[] | null;
  error: string | null;
  loading: boolean;
} {
  const { data, error, loading } = useRealBrokerAccounts<unknown>();
  const nicknameOverrides = useAccountNicknamesStore((s) => s.overrides);
  const accounts = useMemo<BrokerAccount[] | null>(() => {
    if (data === null) return null;
    const renamed = applyAccountNicknames(
      mapRealBrokerAccounts(data),
      nicknameOverrides
    );
    return [...renamed].sort((a, b) => {
      if (a.paper !== b.paper) return a.paper ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  }, [data, nicknameOverrides]);
  return { accounts, error, loading };
}

/**
 * Fetch the per-account detail blob — `/broker-accounts/{id}`.
 *
 * The detail endpoint returns `{portfolio, states, stocks, bots}` in
 * one call: enough to paint the whole Accounts hero (totals, equity
 * curve, positions, attached bots). Pass `null` for the id to idle
 * the hook (e.g. when the selector is on the "All Live" aggregate
 * which doesn't map to a single id). Re-runs whenever the id
 * changes.
 */
export function useRealBrokerAccountDetail(
  id: string | null
): RealDataState<RawBrokerAccountDetail> {
  // Empty path tells `useRealApi` to idle — no network call, no stale
  // value carried over from a prior selection.
  const path = id ? `/broker-accounts/${encodeURIComponent(id)}` : "";
  // Cache key is derived per-id so different accounts don't collide.
  // Idle (null id) uses the empty string — the hook treats missing
  // cache keys as "no cache", which is the behaviour we want.
  const key = id ? cacheKey.detail(id) : undefined;
  return useRealApi<RawBrokerAccountDetail>(path, { cacheKey: key });
}

/**
 * Fetch `/broker-accounts/{id}/financial-activities` — the full lifetime
 * cashflow + dividend/interest/fee log for a single account. The
 * endpoint has **no pagination and no server-side date filter** (see
 * `docs/WHB-API-RECON.md` §5) so every call re-downloads the whole
 * array. For a typical account that's a few hundred rows — fine for
 * direct use, but we still cache results client-side to survive
 * re-logins (cache layer pending).
 *
 * Pass `null` for the id to idle the hook.
 */
export function useRealAccountActivities(
  id: string | null
): RealDataState<RawAccountActivity[]> {
  const path = id
    ? `/broker-accounts/${encodeURIComponent(id)}/financial-activities`
    : "";
  // Activities are a flat array with stable `id`s — the perfect fit
  // for `mergeById`. Every refetch pulls the full lifetime log, but
  // merge-by-id is defensive: if the backend ever starts pruning or
  // paginating, the cache retains rows the new response forgot.
  const key = id ? cacheKey.activities(id) : undefined;
  const raw = useRealApi<unknown>(path, {
    cacheKey: key,
    mergeById: true,
  });
  // Re-wrap the payload so callers always see a typed array (or null
  // while loading). The unwrap filter is cheap and also defends us
  // against the server ever wrapping the response in `{records:[…]}`.
  const data = useMemo<RawAccountActivity[] | null>(() => {
    if (raw.data === null) return null;
    return unwrapActivities(raw.data);
  }, [raw.data]);
  return { ...raw, data };
}

/**
 * Fan-out activities across every real LIVE account in `accounts`.
 *
 * Mirrors the orders/positions aggregate hooks: cache-first paint,
 * parallel network refresh, returns a flat list across all live
 * accounts annotated with their parent `accountId` (the
 * `RawAccountActivity` shape doesn't carry it from the API; we
 * stamp it client-side so downstream filters can scope by account).
 *
 * Sergey 2026-04-30: was the only aggregate tab still on the mock
 * fan-out, which meant real users saw demo activities under "All
 * Live Activities". Now real-aware so single + aggregate render the
 * same row set when there's only one live account.
 */
export function useRealLiveAccountActivities(
  accounts: readonly BrokerAccount[]
): {
  activities: Array<RawAccountActivity & { accountId: string }>;
  loading: boolean;
} {
  const kind = useAuthStore((s) => s.kind);
  const liveIds = useMemo(() => {
    if (kind !== "real") return [] as string[];
    return accounts
      .filter((a) => !a.paper && a.source === "real")
      .map((a) => a.id)
      .sort();
  }, [accounts, kind]);
  const key = liveIds.join(",");

  const [state, setState] = useState<{
    activities: Array<RawAccountActivity & { accountId: string }>;
    loading: boolean;
    key: string;
  }>({ activities: [], loading: false, key: "" });

  useEffect(() => {
    if (kind !== "real" || liveIds.length === 0) {
      setState({ activities: [], loading: false, key });
      return;
    }
    let cancelled = false;
    setState((s) => ({ activities: s.activities, loading: true, key }));

    // Cache-first paint.
    void Promise.all(
      liveIds.map(async (id) => {
        const hit = await cacheGet<unknown>(cacheKey.activities(id));
        if (!hit) return [] as Array<RawAccountActivity & { accountId: string }>;
        const list = unwrapActivities(hit.value);
        return list.map((a) => ({ ...a, accountId: id }));
      })
    ).then((lists) => {
      if (cancelled) return;
      const flat = lists.flat();
      if (flat.length > 0) {
        setState((s) =>
          s.loading ? { activities: flat, loading: true, key } : s
        );
      }
    });

    (async () => {
      const lists = await Promise.all(
        liveIds.map(async (id) => {
          try {
            const raw = await whbFetch<unknown>(
              `/api/broker-accounts/${encodeURIComponent(id)}/financial-activities`
            );
            void cacheSet(cacheKey.activities(id), raw);
            const list = unwrapActivities(raw);
            return list.map((a) => ({ ...a, accountId: id }));
          } catch {
            return [] as Array<RawAccountActivity & { accountId: string }>;
          }
        })
      );
      if (cancelled) return;
      setState({ activities: lists.flat(), loading: false, key });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key]);

  return { activities: state.activities, loading: state.loading };
}

/**
 * Fetch the full trading history for a real account.
 *
 * Server-side pagination exists on this endpoint but we pull the
 * entire account history in one call (`pageSize: 10000`). Reasoning:
 *   • Verified for one account (480 orders) that a single large
 *     request succeeds and beats multi-page UX.
 *   • Orders max out at thousands per account even for heavy users;
 *     at ~500 bytes/row that's still a few hundred KB — fine.
 *   • The PWA's filter bar works client-side, so we need the full set
 *     anyway to keep the "X orders matched" count honest.
 *
 * Idle on demo sessions and when `id` is null. Returns newest-first
 * (matches the API's default sort).
 */
export function useRealAccountOrders(id: string | null): {
  orders: Order[] | null;
  loading: boolean;
  error: string | null;
} {
  const kind = useAuthStore((s) => s.kind);
  const idle = kind !== "real" || !id;
  const [state, setState] = useState<{
    orders: Order[] | null;
    loading: boolean;
    error: string | null;
  }>({ orders: null, loading: !idle, error: null });

  useEffect(() => {
    if (idle) {
      setState({ orders: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    const key = cacheKey.orders(id);
    setState((s) => ({ ...s, loading: true, error: null }));

    // Window for the "missed orders" surface (Sergey 2026-04-30).
    // Sixty days of recent orders catches any post-deletion manual
    // fills the user is likely to care about without dragging the
    // payload size up. Older history is fully covered by
    // `/trading-history` already — the user observed missed orders
    // only show up after a bot is deleted, which is a recent event.
    const missedToIso = new Date().toISOString().slice(0, 10);
    const missedFromIso = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    const missedKey = cacheKey.missedOrders(id);
    const missedOrdersUrl =
      `/api/broker-accounts/${encodeURIComponent(id)}/orders` +
      `?from=${missedFromIso}&to=${missedToIso}`;

    // Helper: merge two order arrays, preferring trading-history
    // entries when both feeds carry the same Alpaca order id. The
    // /trading-history row is the canonical source — it carries bot
    // attribution and broker state — so missed-orders rows only
    // appear when they're NOT already present.
    //
    // Sort newest-first by submittedAt after the merge — without
    // this, missed orders get appended after the entire trading-
    // history (which is internally newest-first) so a brand-new Apr
    // 29 sell ends up rendered BELOW Apr 27 history, looking like
    // it's missing from the top of the list (Sergey 2026-04-30 —
    // diagnosed via the live DOM probe).
    const mergeFeeds = (history: Order[], missed: Order[]): Order[] => {
      const have = new Set(history.map((o) => o.id));
      const dedupedMissed = missed.filter((o) => !have.has(o.id));
      return [...history, ...dedupedMissed].sort((a, b) =>
        (b.submittedAt ?? "").localeCompare(a.submittedAt ?? "")
      );
    };

    // Cache-first paint. Includes any persisted reconciliation
    // orders (orderType: "correction") so the Orders tab shows them
    // immediately, before the network round-trip completes.
    void (async () => {
      const [hit, missedHit, corrections] = await Promise.all([
        cacheGet<RawTradingHistoryResponse>(key),
        cacheGet<unknown>(missedKey),
        loadCorrections(id),
      ]);
      if (cancelled || !hit) return;
      const history = mapRealTradingHistory(hit.value);
      const missed = missedHit ? mapRealMissedOrders(missedHit.value, id) : [];
      const merged = [...mergeFeeds(history, missed), ...corrections];
      setState((s) =>
        s.orders === null
          ? {
              orders: merged,
              loading: true,
              error: null,
            }
          : s
      );
    })();

    (async () => {
      try {
        // Fire trading-history (POST) and missed-orders (GET) in
        // parallel — they share no dependencies, and the merge
        // happens once both have landed (or once one fails, the
        // other still drives the orders feed).
        const [body, missedRaw] = await Promise.all([
          whbFetch<RawTradingHistoryResponse>(`/api/trading-history`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              brokerAccountId: id,
              page: 1,
              // Single-page fetch. See hook docstring for rationale.
              pageSize: 10000,
              // Pull the full lifetime — the PWA filters client-side.
              // Conservative window bounds so the backend can't decide
              // to return a partial set silently. 2020-01-01 safely
              // predates every account we've seen.
              startDate: "2020-01-01",
              endDate: missedToIso,
              symbols: "",
              action: null,
              strategy: null,
              state: null,
              botId: null,
            }),
          }),
          // /broker-accounts/{id}/orders is the "Link Orders" surface
          // — captures manual Alpaca fills that bypassed WHB bots
          // (e.g. user closed positions directly after deleting a
          // bot). Failure here is non-fatal: we still surface the
          // trading-history feed.
          whbFetch<unknown>(missedOrdersUrl).catch((err) => {
            // Log to console for diagnostics; equivalent in spirit
            // to the existing error swallowing on cashflow gaps.
            // eslint-disable-next-line no-console
            console.warn("[missed-orders] fetch failed:", err);
            return null;
          }),
        ]);
        if (cancelled) return;
        // Full write (not merge-by-id) — trading-history is the
        // authoritative snapshot and POST response replaces whatever
        // was there. Re-reading after a trade places a new row into
        // the cache cleanly.
        void cacheSet(key, body);
        if (missedRaw !== null) void cacheSet(missedKey, missedRaw);
        // Merge in persisted reconciliation orders so the Orders tab
        // shows them alongside real broker orders. Corrections live
        // in a separate IndexedDB key (`corrections:{accountId}`)
        // that survives sign-out.
        const corrections = await loadCorrections(id);
        if (cancelled) return;
        const history = mapRealTradingHistory(body);
        const missed = missedRaw ? mapRealMissedOrders(missedRaw, id) : [];
        setState({
          orders: [...mergeFeeds(history, missed), ...corrections],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          orders: s.orders, // keep cached paint visible on failure
          loading: false,
          error: (err as Error).message ?? String(err),
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [idle, id]);

  return state;
}

/**
 * Bulk orders fetch — fires `/trading-history` for every real LIVE
 * account in parallel. Used by the Orders tab in aggregate ("All
 * Live") mode. Results are concatenated and sorted newest-first.
 */
export function useRealLiveAccountOrders(
  accounts: readonly BrokerAccount[]
): {
  orders: Order[];
  loading: boolean;
} {
  const kind = useAuthStore((s) => s.kind);
  const liveIds = useMemo(() => {
    if (kind !== "real") return [] as string[];
    return accounts
      .filter((a) => !a.paper && a.source === "real")
      .map((a) => a.id)
      .sort();
  }, [accounts, kind]);
  const key = liveIds.join(",");

  const [state, setState] = useState<{
    orders: Order[];
    loading: boolean;
    key: string;
  }>({ orders: [], loading: false, key: "" });

  useEffect(() => {
    if (kind !== "real" || liveIds.length === 0) {
      setState({ orders: [], loading: false, key });
      return;
    }
    let cancelled = false;
    setState((s) => ({ orders: s.orders, loading: true, key }));

    // Same per-account merge semantics as `useRealAccountOrders`:
    // history wins on duplicate Alpaca order ids; the missed-orders
    // surface only contributes rows that aren't already in /trading-
    // history. Sergey 2026-04-30: the aggregate hook was only firing
    // /trading-history, so manual fills (post-bot-deletion) silently
    // dropped from the All Live Orders tab.
    const mergeFeeds = (history: Order[], missed: Order[]): Order[] => {
      const have = new Set(history.map((o) => o.id));
      const dedupedMissed = missed.filter((o) => !have.has(o.id));
      return [...history, ...dedupedMissed];
    };

    // 60-day rolling window for the missed-orders surface — same
    // policy as `useRealAccountOrders`, captures any post-deletion
    // manual fills without dragging the payload size up.
    const missedToIso = new Date().toISOString().slice(0, 10);
    const missedFromIso = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    // Cache-first — assemble all three feeds from IndexedDB per account.
    // Sergey 2026-04-30: aggregate was diverging from single because the
    // single hook merged in `loadCorrections(id)` (persisted reconciliation
    // entries the user added through the orders UI) but the aggregate
    // hook skipped them. Three feeds now flow through both hooks identically.
    void Promise.all(
      liveIds.map(async (id) => {
        const [hit, missedHit, corrections] = await Promise.all([
          cacheGet<RawTradingHistoryResponse>(cacheKey.orders(id)),
          cacheGet<unknown>(cacheKey.missedOrders(id)),
          loadCorrections(id),
        ]);
        const history = hit ? mapRealTradingHistory(hit.value) : [];
        const missed = missedHit ? mapRealMissedOrders(missedHit.value, id) : [];
        return [...mergeFeeds(history, missed), ...corrections];
      })
    ).then((lists) => {
      if (cancelled) return;
      const merged = lists.flat();
      if (merged.length > 0) {
        setState((s) =>
          s.loading
            ? {
                orders: merged.sort((a, b) =>
                  b.submittedAt.localeCompare(a.submittedAt)
                ),
                loading: true,
                key,
              }
            : s
        );
      }
    });

    (async () => {
      const endDate = missedToIso;
      const lists = await Promise.all(
        liveIds.map(async (id) => {
          try {
            const [body, missedRaw] = await Promise.all([
              whbFetch<RawTradingHistoryResponse>(`/api/trading-history`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  brokerAccountId: id,
                  page: 1,
                  pageSize: 10000,
                  startDate: "2020-01-01",
                  endDate,
                  symbols: "",
                  action: null,
                  strategy: null,
                  state: null,
                  botId: null,
                }),
              }),
              whbFetch<unknown>(
                `/api/broker-accounts/${encodeURIComponent(id)}/orders` +
                  `?from=${missedFromIso}&to=${missedToIso}`
              ).catch(() => null),
            ]);
            void cacheSet(cacheKey.orders(id), body);
            if (missedRaw !== null) void cacheSet(cacheKey.missedOrders(id), missedRaw);
            const corrections = await loadCorrections(id);
            const history = mapRealTradingHistory(body);
            const missed = missedRaw ? mapRealMissedOrders(missedRaw, id) : [];
            return [...mergeFeeds(history, missed), ...corrections];
          } catch {
            return [] as Order[];
          }
        })
      );
      if (cancelled) return;
      const merged = lists.flat().sort((a, b) =>
        b.submittedAt.localeCompare(a.submittedAt)
      );
      setState({ orders: merged, loading: false, key });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key]);

  return { orders: state.orders, loading: state.loading };
}

/**
 * Fetch open positions for a real account.
 *
 * Pulls the same cached `/broker-accounts/{id}` detail response as
 * `useRealBrokerAccountDetail` (so the two hooks share a single
 * network request + IndexedDB entry) and maps its `stocks[]` array
 * into the PWA's `Position[]` display shape.
 *
 * Pass `null` for the id to idle; demo sessions return idle too.
 */
export function useRealAccountPositions(id: string | null): {
  positions: Position[] | null;
  loading: boolean;
  error: string | null;
} {
  const { data, loading, error } = useRealBrokerAccountDetail(id);
  // Read cached trading-history rows for this account so the adapter
  // can derive each position's "Opened" date from the earliest buy.
  // Cache-first means it paints as soon as the trades cache hits IDB;
  // no extra network. Sergey 2026-05-01.
  const [ordersRaw, setOrdersRaw] = useState<RawTradingHistoryRow[]>([]);
  useEffect(() => {
    if (!id) {
      setOrdersRaw([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const hit = await cacheGet<RawTradingHistoryResponse | RawTradingHistoryRow[]>(
        cacheKey.orders(id)
      );
      if (cancelled) return;
      const raw = hit?.value;
      const arr: RawTradingHistoryRow[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.records)
          ? raw.records!
          : [];
      setOrdersRaw(arr);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);
  const positions = useMemo<Position[] | null>(() => {
    if (!data || !id) return null;
    return mapRealStocksToPositions(data.stocks, id, ordersRaw);
  }, [data, id, ordersRaw]);
  return { positions, loading, error };
}

/**
 * Fetch open positions across every real LIVE account in parallel.
 *
 * Used by the Positions tab in aggregate ("All Live") mode so it can
 * show the union of every account's current holdings. Each id piggybacks
 * on the cached `/broker-accounts/{id}` detail response — no extra
 * network cost beyond what the hero already fetches.
 */
export function useRealLiveAccountPositions(
  accounts: readonly BrokerAccount[]
): {
  positions: Position[];
  loading: boolean;
} {
  const kind = useAuthStore((s) => s.kind);
  const liveIds = useMemo(() => {
    if (kind !== "real") return [] as string[];
    return accounts
      .filter((a) => !a.paper && a.source === "real")
      .map((a) => a.id)
      .sort();
  }, [accounts, kind]);
  const key = liveIds.join(",");

  const [state, setState] = useState<{
    positions: Position[];
    loading: boolean;
    key: string;
  }>({ positions: [], loading: false, key: "" });

  useEffect(() => {
    if (kind !== "real" || liveIds.length === 0) {
      setState({ positions: [], loading: false, key });
      return;
    }
    let cancelled = false;
    setState((s) => ({ positions: s.positions, loading: true, key }));

    // Cache-first paint — assemble from IndexedDB detail entries so
    // the tab renders instantly on reload. Also load each account's
    // cached trading-history rows so the adapter can backfill
    // openedAt from the earliest buy per ticker. Sergey 2026-05-01.
    void Promise.all(
      liveIds.map(async (id) => {
        const [d, ordHit] = await Promise.all([
          cacheGet<RawBrokerAccountDetail>(cacheKey.detail(id)),
          cacheGet<RawTradingHistoryResponse | RawTradingHistoryRow[]>(
            cacheKey.orders(id)
          )
        ]);
        const ordersRaw: RawTradingHistoryRow[] = (() => {
          const v = ordHit?.value;
          if (Array.isArray(v)) return v;
          if (Array.isArray((v as RawTradingHistoryResponse | undefined)?.records))
            return (v as RawTradingHistoryResponse).records!;
          return [];
        })();
        return d ? mapRealStocksToPositions(d.value.stocks, id, ordersRaw) : [];
      })
    ).then((lists) => {
      if (cancelled) return;
      const flat = lists.flat();
      if (flat.length > 0) {
        setState((s) => (s.loading ? { positions: flat, loading: true, key } : s));
      }
    });

    (async () => {
      const lists = await Promise.all(
        liveIds.map(async (id) => {
          try {
            const raw = await whbFetch<RawBrokerAccountDetail>(
              `/api/broker-accounts/${encodeURIComponent(id)}`
            );
            void cacheSet(cacheKey.detail(id), raw);
            // Re-read orders cache for openedAt derivation. May be
            // missing on first paint; mapper falls back to "" when
            // no buy is found for a given symbol.
            const ordHit = await cacheGet<
              RawTradingHistoryResponse | RawTradingHistoryRow[]
            >(cacheKey.orders(id));
            const ordersRaw: RawTradingHistoryRow[] = (() => {
              const v = ordHit?.value;
              if (Array.isArray(v)) return v;
              if (Array.isArray((v as RawTradingHistoryResponse | undefined)?.records))
                return (v as RawTradingHistoryResponse).records!;
              return [];
            })();
            return mapRealStocksToPositions(raw.stocks, id, ordersRaw);
          } catch {
            return [] as Position[];
          }
        })
      );
      if (cancelled) return;
      setState({ positions: lists.flat(), loading: false, key });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key]);

  return { positions: state.positions, loading: state.loading };
}

/**
 * `useRealBrokerAccountDetail` wrapped through the detail adapter.
 *
 * Returns a **Partial<BrokerAccount> overlay**, not a full account —
 * the detail endpoint has no `displayName` and therefore can't tell
 * us the broker label ("Alpaca" / "WhiteHatBots"), so the caller
 * must spread this overlay onto the matching list-row shape:
 *
 *   const account = { ...listRow, ...detailOverlay };
 *
 * `overlay` is `null` until the first response lands for the given id.
 */
export function useRealBrokerAccountDetailAdapted(
  id: string | null
): {
  overlay: Partial<BrokerAccount> | null;
  error: string | null;
  loading: boolean;
} {
  const { data, error, loading } = useRealBrokerAccountDetail(id);
  // Fire activities in parallel with the detail fetch. Pass-through
  // id — both endpoints idle on `null`. Error state from activities
  // is intentionally swallowed: a missing activities array still
  // lets us render the hero (just without real cashflows / ND).
  const { data: activities, loading: activitiesLoading } =
    useRealAccountActivities(id);
  // Real SPY price history is cross-account — one hook invocation
  // feeds every overlay. Missing data falls back to the synthetic
  // walk inside `mapRealBrokerAccountDetail`, so no guard needed.
  const { prices: spyPrices } = useRealSpyPriceSeries();
  // Orders drive the full-history reconstruction (trade fills +
  // which tickers we've held). The hook is already cached in
  // IndexedDB so downstream this is free on repeat views.
  const { orders } = useRealAccountOrders(id);
  // Historical quotes for every ticker the account has ever traded —
  // fed to the reconstruction engine to mark positions to market on
  // each historical day. When the list is empty (no orders yet) the
  // hook idles and `prices` is `{}`.
  const tickers = useMemo(
    () => (orders ? uniqueTickersFromOrders(orders) : []),
    [orders]
  );
  const { prices: historicalPrices, splits: historicalSplits } =
    useRealHistoricalPrices(tickers);
  const overlay = useMemo<Partial<BrokerAccount> | null>(() => {
    if (!data || typeof data !== "object" || typeof data.id !== "string") {
      return null;
    }
    // Pass through everything the adapter can use — it branches
    // internally to pick the full-history reconstruction when all
    // inputs are present.
    return mapRealBrokerAccountDetail(
      data,
      activities,
      spyPrices,
      orders,
      historicalPrices as HistoricalQuotesMap,
      historicalSplits as HistoricalSplitsMap
    );
  }, [data, activities, spyPrices, orders, historicalPrices, historicalSplits]);
  return { overlay, error, loading: loading || activitiesLoading };
}

/**
 * Fetch `/broker-accounts/{id}` detail for **every live real account**
 * in parallel. Used to hydrate the "All Live Accounts" aggregate so
 * `aggregateLiveAccounts` has real equity curves + totals to sum
 * instead of the list-row zero placeholders.
 *
 * Returns an `overlays` map keyed by account id. Missing ids (e.g.
 * a detail fetch that failed) are simply absent from the map — the
 * caller should fall back to the list-row shape for those. The hook
 * idles on demo sessions and when the input list has no live accounts.
 *
 * Implementation notes:
 *   • `key` derived from the sorted live-id list is what retriggers
 *     the effect (not the array identity — that changes on every
 *     render even when the ids haven't).
 *   • One `Promise.all` per trigger instead of N individual fetch
 *     effects, so we avoid the "hooks in a loop" trap.
 *   • Failures are swallowed per-account; the overall hook never
 *     throws upward. The selector's $0 suffix-hide still works as
 *     a fallback UI for unhydrated rows.
 */
export function useRealLiveAccountDetails(
  accounts: readonly BrokerAccount[]
): {
  overlays: Record<string, Partial<BrokerAccount>>;
  loading: boolean;
} {
  const kind = useAuthStore((s) => s.kind);
  // SPY price history is shared across every live-account overlay —
  // fetch once up here and thread through. Changes to `prices` force
  // a rebuild of the overlays so real SPY replaces the synthetic
  // walk as soon as the series lands.
  const { prices: spyPrices } = useRealSpyPriceSeries();
  // Only real live accounts need detail fetching — mock accounts
  // already have curves baked in, paper accounts aren't in the
  // aggregate anyway.
  const liveIds = useMemo(() => {
    if (kind !== "real") return [] as string[];
    return accounts
      .filter((a) => !a.paper && a.source === "real")
      .map((a) => a.id)
      .sort();
  }, [accounts, kind]);
  const key = liveIds.join(",");
  // SPY freshness key — when the price series updates we want to
  // rebuild overlays even if the account id set hasn't changed. A
  // cheap proxy is the last date in the series (changes once a
  // trading day). Using `length` alone would miss same-length refreshes.
  const spyKey = spyPrices
    ? `${spyPrices.length}:${spyPrices[spyPrices.length - 1]?.date ?? ""}`
    : "";

  const [state, setState] = useState<{
    overlays: Record<string, Partial<BrokerAccount>>;
    loading: boolean;
    key: string;
  }>({ overlays: {}, loading: false, key: "" });

  useEffect(() => {
    if (kind !== "real" || liveIds.length === 0) {
      setState({ overlays: {}, loading: false, key });
      return;
    }
    let cancelled = false;
    setState((s) => ({ overlays: s.overlays, loading: true, key }));

    // Paint any cached overlays first so the aggregate hero renders
    // real numbers immediately on reload. Each account's detail +
    // activities pair is cached separately so a partial hit (say
    // detail cached, activities not yet) still contributes.
    void Promise.all(
      liveIds.map(async (id) => {
        const [d, a] = await Promise.all([
          cacheGet<RawBrokerAccountDetail>(cacheKey.detail(id)),
          cacheGet<unknown>(cacheKey.activities(id)),
        ]);
        if (!d) return null;
        const acts = a ? unwrapActivities(a.value) : null;
        return [
          id,
          mapRealBrokerAccountDetail(d.value, acts, spyPrices),
        ] as const;
      })
    ).then((cachedResults) => {
      if (cancelled) return;
      const cachedOverlays: Record<string, Partial<BrokerAccount>> = {};
      for (const r of cachedResults) {
        if (r) cachedOverlays[r[0]] = r[1];
      }
      if (Object.keys(cachedOverlays).length > 0) {
        setState((s) =>
          // Don't trample a fresh network result if it's already landed.
          s.loading ? { overlays: cachedOverlays, loading: true, key } : s
        );
      }
    });

    (async () => {
      // Four fetches per account (detail + activities + trading-history
      // + missed orders) dispatched in parallel. Orders feed the
      // full-history reconstruction; activities alone are enough for
      // the hero if the reconstruction inputs fail. Each endpoint
      // failure is swallowed so one bad request doesn't starve the
      // others.
      //
      // Sergey 2026-04-30: missed orders MUST flow into the recon for
      // accounts whose bots have been deleted — without them, the
      // recon's bare cash trajectory diverges from the broker's by
      // the value of the orphan sells (the deleted bot's
      // liquidations). The single-account hook already merges them;
      // mirror here so single + aggregate produce identical equity
      // curves.
      const missedFromIso = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      const missedToIso = new Date().toISOString().slice(0, 10);
      const perAccount = await Promise.all(
        liveIds.map(async (id) => {
          try {
            const [detailRaw, actsRaw, ordersRaw, missedRaw] = await Promise.all([
              whbFetch<RawBrokerAccountDetail>(
                `/api/broker-accounts/${encodeURIComponent(id)}`
              ),
              whbFetch<unknown>(
                `/api/broker-accounts/${encodeURIComponent(id)}/financial-activities`
              ).catch(() => null),
              whbFetch<RawTradingHistoryResponse>(`/api/trading-history`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  brokerAccountId: id,
                  page: 1,
                  pageSize: 10000,
                  startDate: "2020-01-01",
                  endDate: missedToIso,
                  symbols: "",
                  action: null,
                  strategy: null,
                  state: null,
                  botId: null,
                }),
              }).catch(() => null),
              whbFetch<unknown>(
                `/api/broker-accounts/${encodeURIComponent(id)}/orders` +
                  `?from=${missedFromIso}&to=${missedToIso}`
              ).catch(() => null),
            ]);
            // Cache: detail whole-replace, activities merge-by-id,
            // orders whole-replace, missed-orders whole-replace.
            void cacheSet(cacheKey.detail(id), detailRaw);
            const actsArr = unwrapActivities(actsRaw);
            if (actsArr.length > 0) {
              void cacheMergeById(cacheKey.activities(id), actsArr);
            }
            const historyOrders = ordersRaw ? mapRealTradingHistory(ordersRaw) : [];
            if (ordersRaw) void cacheSet(cacheKey.orders(id), ordersRaw);
            const missedOrders = missedRaw ? mapRealMissedOrders(missedRaw, id) : [];
            if (missedRaw !== null) void cacheSet(cacheKey.missedOrders(id), missedRaw);
            // Dedup: trading-history wins on Alpaca order id collisions
            // (it's authoritative — carries bot attribution + state).
            // Missed orders only appear when their externalOrderId
            // isn't already in /trading-history. Same merge semantics
            // as `useRealAccountOrders.mergeFeeds`.
            const have = new Set(historyOrders.map((o) => o.id));
            const dedupedMissed = missedOrders.filter((o) => !have.has(o.id));
            const mappedOrders =
              historyOrders.length === 0 && dedupedMissed.length === 0
                ? null
                : [...historyOrders, ...dedupedMissed];
            return { id, detailRaw, actsArr, mappedOrders };
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;

      // Union of every ticker across every account — one quotes
      // fetch covers the whole aggregate. The `/api/market/quotes`
      // endpoint caches per-ticker at the edge so duplicates across
      // accounts are free.
      const allTickers = new Set<string>();
      for (const r of perAccount) {
        if (!r?.mappedOrders) continue;
        for (const t of uniqueTickersFromOrders(r.mappedOrders)) {
          allTickers.add(t);
        }
      }
      let quotes: HistoricalQuotesMap = {};
      let splits: HistoricalSplitsMap = {};
      if (allTickers.size > 0) {
        // Batch to stay under the server's 10-ticker cap (each server
        // invocation must fit into Cloudflare Workers' 50-subrequest
        // Free-plan budget with the Massive → FMP → Yahoo waterfall;
        // see `useRealHistoricalPrices` for the budget math). Partial
        // failure per-batch is tolerated.
        const allList = Array.from(allTickers).sort();
        const BATCH_SIZE = 10;
        const batches: string[][] = [];
        for (let i = 0; i < allList.length; i += BATCH_SIZE) {
          batches.push(allList.slice(i, i + BATCH_SIZE));
        }
        const mergedPrices: Record<string, QuotePoint[]> = {};
        const mergedSplits: Record<string, TickerSplit[]> = {};
        await Promise.all(
          batches.map(async (group) => {
            try {
              const qr = await fetch(
                `/api/market/quotes?tickers=${encodeURIComponent(group.join(","))}`
              );
              if (!qr.ok) {
                console.warn(
                  `[whb-data:bulk] /api/market/quotes ${qr.status} for [${group.join(",")}]`
                );
                return;
              }
              const body = (await qr.json()) as QuotesResponse & { error?: string };
              if (body.error) {
                console.warn(
                  `[whb-data:bulk] /api/market/quotes returned error for [${group.join(",")}]: ${body.error}`
                );
              }
              if (!body?.quotes) return;
              const splitMap = body.splits ?? {};
              const sourceMap = body.sources ?? {};
              for (const t of Object.keys(body.quotes)) {
                const pts = body.quotes[t];
                if (Array.isArray(pts) && pts.length > 0) {
                  mergedPrices[t] = pts;
                  const s = Array.isArray(splitMap[t]) ? splitMap[t] : [];
                  if (s.length > 0) mergedSplits[t] = s;
                  if (sourceMap[t]) quoteSourceRegistry[t] = sourceMap[t];
                }
              }
            } catch {
              // Per-batch failure is tolerated.
            }
          })
        );
        quotes = mergedPrices as HistoricalQuotesMap;
        splits = mergedSplits as HistoricalSplitsMap;
        // Stash combined {points, splits} in IndexedDB so the single-
        // account hook (useRealHistoricalPrices) paints instantly on
        // drill-in with split metadata intact.
        for (const t of Object.keys(quotes)) {
          const s = mergedSplits[t] ?? [];
          void cacheSet(cacheKey.tickerQuotes(t), { points: quotes[t], splits: s });
        }
      }
      if (cancelled) return;

      const overlays: Record<string, Partial<BrokerAccount>> = {};
      for (const r of perAccount) {
        if (!r) continue;
        overlays[r.id] = mapRealBrokerAccountDetail(
          r.detailRaw,
          r.actsArr,
          spyPrices,
          r.mappedOrders ?? null,
          quotes,
          splits
        );
      }
      setState({ overlays, loading: false, key });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key, spyKey]);

  return { overlays: state.overlays, loading: state.loading };
}

/**
 * Merge `useRealLiveAccountDetails`'s overlays onto the input accounts
 * list so each live-real row ends up with its real hero numbers +
 * curves + bot ids. Paper rows pass through untouched (still have
 * zero placeholders — they aren't part of the aggregate anyway).
 *
 * Returns the same reference until the overlays or accounts list
 * changes, so it's safe to pass into `useMemo` deps downstream.
 */
export function useRealLiveAccountsMerged(
  accounts: readonly BrokerAccount[]
): {
  accounts: BrokerAccount[];
  loading: boolean;
} {
  const { overlays, loading } = useRealLiveAccountDetails(accounts);
  const merged = useMemo<BrokerAccount[]>(() => {
    if (Object.keys(overlays).length === 0) return accounts.slice();
    return accounts.map((a) => {
      const overlay = overlays[a.id];
      return overlay ? { ...a, ...overlay } : a;
    });
  }, [accounts, overlays]);
  return { accounts: merged, loading };
}
